import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Medal, Edit2, Check, X, Lock, Shield } from 'lucide-react';
import dataGolfService, { normalizeName } from './dataGolfService';
import firebaseService from './firebaseService';
import { getGolferCourseHistory } from './courseHistory';
import { SEASON_ROSTERS, getGolferOwner } from './seasonRosters';

const TOURNAMENTS = [
  { id: 1, name: 'Genesis Invitational', tournamentId: 'genesis-invitational', type: 'signature', date: '2026-02-12', completed: true, course: 'The Riviera Country Club', location: 'Pacific Palisades, CA', par: 71, yardage: 7245 },
  { id: 2, name: 'Arnold Palmer Invitational', tournamentId: 'arnold-palmer-invitational', type: 'signature', date: '2026-03-05', completed: true, course: "Arnold Palmer's Bay Hill Club & Lodge", location: 'Orlando, FL', par: 72, yardage: 7349 },
  { id: 3, name: 'The Players Championship', tournamentId: 'the-players-championship', type: 'special', date: '2026-03-12', completed: true, course: 'TPC Sawgrass (THE PLAYERS Stadium Course)', location: 'Ponte Vedra Beach, FL', par: 72, yardage: 7135 },
  { id: 4, name: 'The Masters', tournamentId: 'the-masters', type: 'major', date: '2026-04-09', completed: true, course: 'Augusta National Golf Club', location: 'Augusta, GA', par: 72, yardage: 7479 },
  { id: 5, name: 'RBC Heritage', tournamentId: 'rbc-heritage', type: 'signature', date: '2026-04-16', completed: false, course: 'Harbour Town Golf Links', location: 'Hilton Head Island, SC', par: 71, yardage: 6980 },
  { id: 6, name: 'PGA Championship', tournamentId: 'pga-championship', type: 'major', date: '2026-05-14', completed: false, course: 'Aronimink GC', location: 'Newtown Square, PA', par: 70, yardage: 7036 },
  { id: 7, name: 'The Memorial', tournamentId: 'the-memorial', type: 'signature', date: '2026-06-04', completed: false, course: 'Muirfield Village Golf Club', location: 'Dublin, OH', par: 72, yardage: 7366 },
  { id: 8, name: 'U.S. Open', tournamentId: 'us-open', type: 'major', date: '2026-06-18', completed: false, course: 'Shinnecock Hills Golf Club', location: 'Southampton, NY', par: 70, yardage: 7445 },
  { id: 9, name: 'The Open Championship', tournamentId: 'the-open-championship', type: 'major', date: '2026-07-16', completed: false, course: 'Royal Birkdale', location: 'Southport, England', par: 72, yardage: 7170 },
  { id: 10, name: 'BMW Championship', tournamentId: 'bmw-championship', type: 'signature', date: '2026-08-20', completed: false, course: 'Bellerive Country Club', location: 'St. Louis, MO', par: 70, yardage: 7248 }
];

// Helper to build a golfer object with draft status from seasonRosters
const buildGolfer = (id, name, owgr) => {
  const owner = getGolferOwner(name);
  return {
    id,
    name,
    owgr,
    seasonWinnings: 0,
    drafted: !!owner,
    draftedBy: owner
  };
};

const INITIAL_GOLFERS = [
  buildGolfer(1, 'Scottie Scheffler', 1),
  buildGolfer(2, 'Rory McIlroy', 2),
  buildGolfer(3, 'Ludvig Aberg', 3),
  buildGolfer(4, 'Collin Morikawa', 4),
  buildGolfer(5, 'Xander Schauffele', 5),
  buildGolfer(6, 'Hideki Matsuyama', 6),
  buildGolfer(7, 'Jon Rahm', 7),
  buildGolfer(8, 'Patrick Cantlay', 8),
  buildGolfer(9, 'Viktor Hovland', 9),
  buildGolfer(10, 'Sahith Theegala', 10),
  buildGolfer(11, 'Bryson DeChambeau', 11),
  buildGolfer(12, 'Sungjae Im', 12),
  buildGolfer(13, 'Keegan Bradley', 13),
  buildGolfer(14, 'Robert MacIntyre', 14),
  buildGolfer(15, 'Tommy Fleetwood', 15),
  buildGolfer(16, 'Russell Henley', 16),
  buildGolfer(17, 'Akshay Bhatia', 17),
  buildGolfer(18, 'Brooks Koepka', 18),
  buildGolfer(19, 'Wyndham Clark', 19),
  buildGolfer(20, 'Sam Burns', 20),
  buildGolfer(21, 'Max Homa', 21),
  buildGolfer(22, 'Billy Horschel', 22),
  buildGolfer(23, 'Justin Thomas', 23),
  buildGolfer(24, 'Adam Scott', 24),
  buildGolfer(25, 'Tony Finau', 25),
  buildGolfer(26, 'Corey Conners', 26),
  buildGolfer(27, 'Shane Lowry', 27),
  buildGolfer(28, 'Aaron Rai', 28),
  buildGolfer(29, 'Matt Fitzpatrick', 29),
  buildGolfer(30, 'Alex Noren', 30),
  buildGolfer(31, 'Justin Rose', 31),
  buildGolfer(32, 'Jason Day', 32),
  buildGolfer(33, 'Taylor Pendrith', 33),
  buildGolfer(34, 'Cameron Smith', 34),
  buildGolfer(35, 'Tom Kim', 35),
  buildGolfer(36, 'Christiaan Bezuidenhout', 36),
  buildGolfer(37, 'Matthieu Pavon', 37),
  buildGolfer(38, 'Min Woo Lee', 38),
  buildGolfer(39, 'Sepp Straka', 39),
  buildGolfer(40, 'Maverick McNealy', 40),
  buildGolfer(41, 'Adam Hadwin', 41),
  buildGolfer(42, 'Denny McCarthy', 42),
  buildGolfer(43, 'Si Woo Kim', 43),
  buildGolfer(44, 'Thomas Detry', 44),
  buildGolfer(45, 'Stephan Jaeger', 45),
  buildGolfer(46, 'J.T. Poston', 46),
  buildGolfer(47, 'Harris English', 47),
  buildGolfer(48, 'Tyrrell Hatton', 48),
  buildGolfer(49, 'Austin Eckroat', 49),
  buildGolfer(50, 'Nick Taylor', 50),
  buildGolfer(51, 'Chris Kirk', 51),
  buildGolfer(52, 'Cameron Young', 52),
  buildGolfer(53, 'Eric Cole', 53),
  buildGolfer(54, 'Byeong Hun An', 54),
  buildGolfer(55, 'Jordan Spieth', 55),
  buildGolfer(56, 'Davis Thompson', 56),
  buildGolfer(57, 'Joaquin Niemann', 57),
  buildGolfer(58, 'Mackenzie Hughes', 58),
  buildGolfer(59, 'Emiliano Grillo', 59),
  buildGolfer(60, 'Sebastian Soderberg', 60),
  buildGolfer(61, 'Patrick Rodgers', 61),
  buildGolfer(62, 'Will Zalatoris', 62),
  buildGolfer(63, 'Thorbjorn Olesen', 63),
  buildGolfer(64, 'Adrian Meronk', 64),
  buildGolfer(65, 'Jake Knapp', 65),
  buildGolfer(66, 'Nick Dunlap', 66),
  buildGolfer(67, 'Talor Gooch', 67),
  buildGolfer(68, 'Min-Kyu Kim', 68),
  buildGolfer(69, 'Matt Wallace', 69),
  buildGolfer(70, 'Ryo Hisatsune', 70),
  buildGolfer(71, 'Rasmus Hojgaard', 71),
  buildGolfer(72, 'Kurt Kitayama', 72),
  buildGolfer(73, 'Rickie Fowler', 73),
  buildGolfer(74, 'Camilo Villegas', 74),
  buildGolfer(75, 'Andrew Putnam', 75),
  buildGolfer(76, 'Min Chel Choi', 76),
  buildGolfer(77, 'Victor Perez', 77),
  buildGolfer(78, 'Ben Griffin', 78),
  buildGolfer(79, 'Nico Echavarria', 79),
  buildGolfer(80, 'Dean Burmester', 80),
  buildGolfer(81, 'Tom Hoge', 81),
  buildGolfer(82, 'Lee Hodges', 82),
  buildGolfer(83, 'Nicolai Hojgaard', 83),
  buildGolfer(84, 'Brendon Todd', 84),
  buildGolfer(85, 'Doug Ghim', 85),
  buildGolfer(86, 'Alex Smalley', 86),
  buildGolfer(87, 'Francesco Molinari', 87),
  buildGolfer(88, 'Luke List', 88),
  buildGolfer(89, 'Matt Kuchar', 89),
  buildGolfer(90, 'Charley Hoffman', 90),
  buildGolfer(91, 'Joel Dahmen', 91),
  buildGolfer(92, 'Gary Woodland', 92),
  buildGolfer(93, 'Kevin Kisner', 93),
  buildGolfer(94, 'Webb Simpson', 94),
  buildGolfer(95, 'Scott Stallings', 95),
  buildGolfer(96, 'Beau Hossler', 96),
  buildGolfer(97, 'Taylor Moore', 97),
  buildGolfer(98, 'Keith Mitchell', 98),
  buildGolfer(99, 'Zach Johnson', 99),
  buildGolfer(100, 'Danny Willett', 100),
  buildGolfer(101, 'Davis Riley', 101),
  buildGolfer(102, 'Michael Kim', 102),
  buildGolfer(103, 'Michael Thorbjornsen', 103),
  buildGolfer(104, 'Aldrich Potgieter', 104),
  buildGolfer(105, 'Patrick Reed', 105),
  buildGolfer(106, 'Ryan Gerard', 106),
  buildGolfer(107, 'Ryan Fox', 107),
  buildGolfer(108, 'Max Greyserman', 108),
  buildGolfer(109, 'Rasmus Neergaard-Petersen', 109),
  buildGolfer(110, 'Lucas Glover', 110),
  buildGolfer(111, 'Daniel Berger', 111),
  buildGolfer(112, 'Haotong Li', 112),
  buildGolfer(113, 'Chris Gotterup', 113),
  buildGolfer(114, 'Matt McCarty', 114),
  buildGolfer(115, 'Marco Penge', 115),
  buildGolfer(116, 'Harry Hall', 116),
  buildGolfer(117, 'John Keefer', 117),
  buildGolfer(118, 'Sami Valimaki', 118),
  buildGolfer(119, 'J.J. Spaun', 119),
  buildGolfer(120, 'Pierceson Coody', 120),
  buildGolfer(121, 'Jackson Koivun(Am)', 121),
  buildGolfer(122, 'Brian Harman', 122),
  buildGolfer(123, 'Andrew Novak', 123),
  buildGolfer(124, 'Kristoffer Reitan', 124),
];

// Hardcoded tournament results from Genesis and Arnold Palmer
const HARDCODED_HISTORY = {
  1: { // Genesis Invitational
    'Jedlicka': { earnings: 2936500, majorEarnings: 0, lineup: ['Rory McIlroy', 'Aaron Rai', 'Keegan Bradley', 'Adam Scott'] },
    'Moy': { earnings: 934000, majorEarnings: 0, lineup: ['Ludvig Aberg', 'Robert MacIntyre', 'Michael Kim', 'Jordan Spieth'] },
    'Starr': { earnings: 1139200, majorEarnings: 0, lineup: ['Xander Schauffele', 'Alex Noren', 'Taylor Pendrith', 'Denny McCarthy'] },
    'Drozd': { earnings: 712200, majorEarnings: 0, lineup: ['Tommy Fleetwood', 'Si Woo Kim', 'Jason Day', 'J.T. Poston'] },
    'Ben': { earnings: 316750, majorEarnings: 0, lineup: ['Harris English', 'Sam Burns', 'Corey Conners', 'Tyrrell Hatton'] },
    'Chin': { earnings: 817700, majorEarnings: 0, lineup: ['Justin Rose', 'Ben Griffin', 'Ryan Gerard', 'Ryan Fox'] },
    'Stumpf': { earnings: 306750, majorEarnings: 0, lineup: ['Viktor Hovland', 'Patrick Cantlay', 'Rickie Fowler', 'Lucas Glover'] },
    'Hoffmann': { earnings: 2114750, majorEarnings: 0, lineup: ['Hideki Matsuyama', 'Matt Fitzpatrick', 'Kurt Kitayama', 'Daniel Berger'] },
    'Noonan': { earnings: 1508900, majorEarnings: 0, lineup: ['Collin Morikawa', 'Cameron Young', 'Sahith Theegala', 'Wyndham Clark'] },
    'Stack': { earnings: 178250, majorEarnings: 0, lineup: ['Russell Henley', 'Chris Gotterup', 'Matt McCarty', 'Harry Hall'] },
    "O'Hare": { earnings: 415000, majorEarnings: 0, lineup: ['J.J. Spaun', 'Maverick McNealy', 'Min Woo Lee', 'Tom Hoge'] },
    'McEldowney': { earnings: 1126250, majorEarnings: 0, lineup: ['Jake Knapp', 'Sepp Straka', 'Shane Lowry', 'Nick Taylor'] }
  },
  2: { // Arnold Palmer Invitational
    'Jedlicka': { earnings: 493000, majorEarnings: 0, lineup: ['Rory McIlroy', 'Joel Dahmen', 'Adam Scott', 'Keegan Bradley'] },
    'Moy': { earnings: 1959000, majorEarnings: 0, lineup: ['Ludvig Aberg', 'Robert MacIntyre', 'Michael Kim', 'Jordan Spieth'] },
    'Starr': { earnings: 404000, majorEarnings: 0, lineup: ['Xander Schauffele', 'Alex Noren', 'Taylor Pendrith', 'Denny McCarthy'] },
    'Drozd': { earnings: 429200, majorEarnings: 0, lineup: ['Tommy Fleetwood', 'Si Woo Kim', 'Jason Day', 'Sungjae Im'] },
    'Ben': { earnings: 266000, majorEarnings: 0, lineup: ['Harris English', 'Sam Burns', 'Corey Conners', 'Stephan Jaeger'] },
    'Chin': { earnings: 157000, majorEarnings: 0, lineup: ['Justin Rose', 'Ryan Gerard', 'Ben Griffin', 'Ryan Fox'] },
    'Stumpf': { earnings: 1108200, majorEarnings: 0, lineup: ['Viktor Hovland', 'Patrick Cantlay', 'Rickie Fowler', 'Lucas Glover'] },
    'Hoffmann': { earnings: 2617000, majorEarnings: 0, lineup: ['Hideki Matsuyama', 'Matt Fitzpatrick', 'Kurt Kitayama', 'Daniel Berger'] },
    'Noonan': { earnings: 2040000, majorEarnings: 0, lineup: ['Collin Morikawa', 'Cameron Young', 'Tony Finau', 'Wyndham Clark'] },
    'Stack': { earnings: 1619000, majorEarnings: 0, lineup: ['Russell Henley', 'Chris Gotterup', 'Matt McCarty', 'Harry Hall'] },
    "O'Hare": { earnings: 1075200, majorEarnings: 0, lineup: ['J.J. Spaun', 'Maverick McNealy', 'Min Woo Lee', 'Pierceson Coody'] },
    'McEldowney': { earnings: 517200, majorEarnings: 0, lineup: ['Shane Lowry', 'Sepp Straka', 'Brian Harman', 'Nick Taylor'] }
  },
  3: { // The Players Championship
    'Jedlicka': { earnings: 149437, majorEarnings: 0, lineup: ['Rory McIlroy', 'Aaron Rai', 'Keegan Bradley', 'Adam Scott'] },
    'Moy': { earnings: 2506437, majorEarnings: 0, lineup: ['Ludvig Aberg', 'Robert MacIntyre', 'Michael Thorbjornsen', 'Jordan Spieth'] },
    'Starr': { earnings: 1879874, majorEarnings: 0, lineup: ['Xander Schauffele', 'Alex Noren', 'Denny McCarthy', 'Taylor Pendrith'] },
    'Drozd': { earnings: 819500, majorEarnings: 0, lineup: ['Tommy Fleetwood', 'Si Woo Kim', 'Jason Day', 'Sungjae Im'] },
    'Ben': { earnings: 800681, majorEarnings: 0, lineup: ['Harris English', 'Sam Burns', 'Corey Conners', 'Stephan Jaeger'] },
    'Chin': { earnings: 535778, majorEarnings: 0, lineup: ['Justin Rose', 'Ben Griffin', 'Rasmus Hojgaard', 'Ryan Gerard'] },
    'Stumpf': { earnings: 1269215, majorEarnings: 0, lineup: ['Viktor Hovland', 'Patrick Cantlay', 'Justin Thomas', 'Rickie Fowler'] },
    'Hoffmann': { earnings: 3041000, majorEarnings: 0, lineup: ['Hideki Matsuyama', 'Matt Fitzpatrick', 'Kurt Kitayama', 'Nicolai Hojgaard'] },
    'Noonan': { earnings: 5044215, majorEarnings: 0, lineup: ['Sahith Theegala', 'Cameron Young', 'Akshay Bhatia', 'Wyndham Clark'] },
    'Stack': { earnings: 424653, majorEarnings: 0, lineup: ['Russell Henley', 'Chris Gotterup', 'Matt McCarty', 'Marco Penge'] },
    "O'Hare": { earnings: 411624, majorEarnings: 0, lineup: ['J.J. Spaun', 'Maverick McNealy', 'Min Woo Lee', 'Pierceson Coody'] },
    'McEldowney': { earnings: 790625, majorEarnings: 0, lineup: ['Shane Lowry', 'Sepp Straka', 'Jake Knapp', 'Nick Taylor'] }
  },
  4: { // The Masters
    'Jedlicka': { earnings: 4995900, majorEarnings: 4995900, lineup: ['Rory McIlroy', 'Adam Scott', 'Aaron Rai', 'Keegan Bradley'] },
    'Moy': { earnings: 679500, majorEarnings: 679500, lineup: ['Ludvig Aberg', 'Bryson DeChambeau', 'Robert MacIntyre', 'Jordan Spieth'] },
    'Starr': { earnings: 1203750, majorEarnings: 1203750, lineup: ['Xander Schauffele', 'Brooks Koepka', 'Alex Noren', 'Will Zalatoris'] },
    'Drozd': { earnings: 618750, majorEarnings: 618750, lineup: ['Tommy Fleetwood', 'Jason Day', 'Sungjae Im', 'Cameron Smith'] },
    'Ben': { earnings: 2379375, majorEarnings: 2379375, lineup: ['Harris English', 'Sam Burns', 'Patrick Reed', 'Tyrrell Hatton'] },
    'Chin': { earnings: 1254600, majorEarnings: 1254600, lineup: ['Justin Rose', 'Ben Griffin', 'Rasmus Hojgaard', 'Ryan Fox'] },
    'Stumpf': { earnings: 1455750, majorEarnings: 1455750, lineup: ['Viktor Hovland', 'Patrick Cantlay', 'Justin Thomas', 'Max Homa'] },
    'Hoffmann': { earnings: 843750, majorEarnings: 843750, lineup: ['Hideki Matsuyama', 'Matt Fitzpatrick', 'Jon Rahm', 'Nicolai Hojgaard'] },
    'Noonan': { earnings: 1332000, majorEarnings: 1332000, lineup: ['Cameron Young', 'Akshay Bhatia', 'Wyndham Clark', 'Tony Finau'] },
    'Stack': { earnings: 1319850, majorEarnings: 1319850, lineup: ['Russell Henley', 'Chris Gotterup', 'Marco Penge', 'Harry Hall'] },
    "O'Hare": { earnings: 315000, majorEarnings: 315000, lineup: ['J.J. Spaun', 'Maverick McNealy', 'Min Woo Lee'] },
    'McEldowney': { earnings: 875250, majorEarnings: 875250, lineup: ['Shane Lowry', 'Sepp Straka', 'Nick Taylor', 'Jake Knapp'] }
  }
};

// Hardcoded weekly lineups (golfer IDs) for completed + upcoming tournaments
const HARDCODED_LINEUPS = {
  'Jedlicka': { 1: [2, 28, 13, 24], 2: [2, 91, 24, 13], 3: [2, 28, 13, 24] },
  'Moy': { 1: [3, 14, 102, 55], 2: [3, 14, 102, 55], 3: [3, 14, 103, 55] },
  'Starr': { 1: [5, 30, 33, 42], 2: [5, 30, 33, 42], 3: [5, 30, 42, 33] },
  'Drozd': { 1: [15, 43, 32, 46], 2: [15, 43, 32, 12], 3: [15, 43, 32, 12] },
  'Ben': { 1: [47, 20, 26, 48], 2: [47, 20, 26, 45], 3: [47, 20, 26, 45] },
  'Chin': { 1: [31, 78, 106, 107], 2: [31, 106, 78, 107], 3: [31, 78, 71, 106] },
  'Stumpf': { 1: [9, 8, 73, 110], 2: [9, 8, 73, 110], 3: [9, 8, 23, 73] },
  'Hoffmann': { 1: [6, 29, 72, 111], 2: [6, 29, 72, 111], 3: [6, 29, 72, 83] },
  'Noonan': { 1: [4, 52, 10, 19], 2: [4, 52, 25, 19], 3: [10, 52, 17, 19] },
  'Stack': { 1: [16, 113, 114, 116], 2: [16, 113, 114, 116], 3: [16, 113, 114, 115] },
  "O'Hare": { 1: [119, 40, 38, 81], 2: [119, 40, 38, 120], 3: [119, 40, 38, 120] },
  'McEldowney': { 1: [65, 39, 27, 50], 2: [27, 39, 122, 50], 3: [27, 39, 65, 50] }
};

// Late-round draft picks (rounds 5, 6, 7) — these players are tracked for "Top Value Picks"
const FIRST_ROUND_PICKS = {
  'Rory McIlroy': { owner: 'Jedlicka', pick: 1 },
  'Ludvig Aberg': { owner: 'Moy', pick: 2 },
  'Xander Schauffele': { owner: 'Starr', pick: 3 },
  'Tommy Fleetwood': { owner: 'Drozd', pick: 4 },
  'Harris English': { owner: 'Ben', pick: 5 },
  'Justin Rose': { owner: 'Chin', pick: 6 },
  'Viktor Hovland': { owner: 'Stumpf', pick: 7 },
  'Hideki Matsuyama': { owner: 'Hoffmann', pick: 8 },
  'Collin Morikawa': { owner: 'Noonan', pick: 9 },
  'Russell Henley': { owner: 'Stack', pick: 10 },
  'J.J. Spaun': { owner: "O'Hare", pick: 11 },
  'Shane Lowry': { owner: 'McEldowney', pick: 12 },
};

const LATE_ROUND_PICKS = {
  // Round 5
  'Davis Riley': { owner: 'Jedlicka', round: 5, pick: 49 },
  'Michael Kim': { owner: 'Moy', round: 5, pick: 50 },
  'Denny McCarthy': { owner: 'Starr', round: 5, pick: 51 },
  'J.T. Poston': { owner: 'Drozd', round: 5, pick: 52 },
  'Patrick Reed': { owner: 'Ben', round: 5, pick: 53 },
  'Rasmus Hojgaard': { owner: 'Chin', round: 5, pick: 54 },
  'Lucas Glover': { owner: 'Stumpf', round: 5, pick: 55 },
  'Daniel Berger': { owner: 'Hoffmann', round: 5, pick: 56 },
  'Sahith Theegala': { owner: 'Noonan', round: 5, pick: 57 },
  'Harry Hall': { owner: 'Stack', round: 5, pick: 58 },
  'Tom Hoge': { owner: "O'Hare", round: 5, pick: 59 },
  'Nick Taylor': { owner: 'McEldowney', round: 5, pick: 60 },
  // Round 6
  'Jake Knapp': { owner: 'McEldowney', round: 6, pick: 61 },
  'Jackson Koivun(Am)': { owner: "O'Hare", round: 6, pick: 62 },
  'John Keefer': { owner: 'Stack', round: 6, pick: 63 },
  'Tony Finau': { owner: 'Noonan', round: 6, pick: 64 },
  'Nicolai Hojgaard': { owner: 'Hoffmann', round: 6, pick: 65 },
  'Max Homa': { owner: 'Stumpf', round: 6, pick: 66 },
  'Max Greyserman': { owner: 'Chin', round: 6, pick: 67 },
  'Stephan Jaeger': { owner: 'Ben', round: 6, pick: 68 },
  'Aldrich Potgieter': { owner: 'Drozd', round: 6, pick: 69 },
  'Will Zalatoris': { owner: 'Starr', round: 6, pick: 70 },
  'Michael Thorbjornsen': { owner: 'Moy', round: 6, pick: 71 },
  'Joel Dahmen': { owner: 'Jedlicka', round: 6, pick: 72 },
  // Round 7
  'Beau Hossler': { owner: 'Jedlicka', round: 7, pick: 73 },
  'Eric Cole': { owner: 'Moy', round: 7, pick: 74 },
  'Ryo Hisatsune': { owner: 'Starr', round: 7, pick: 75 },
  'Cameron Smith': { owner: 'Drozd', round: 7, pick: 76 },
  'Thomas Detry': { owner: 'Ben', round: 7, pick: 77 },
  'Rasmus Neergaard-Petersen': { owner: 'Chin', round: 7, pick: 78 },
  'Mackenzie Hughes': { owner: 'Stumpf', round: 7, pick: 79 },
  'Haotong Li': { owner: 'Hoffmann', round: 7, pick: 80 },
  'Billy Horschel': { owner: 'Noonan', round: 7, pick: 81 },
  'Sami Valimaki': { owner: 'Stack', round: 7, pick: 82 },
  'Keith Mitchell': { owner: "O'Hare", round: 7, pick: 83 },
  'Kristoffer Reitan': { owner: 'McEldowney', round: 7, pick: 84 },
};

// Individual golfer earnings per tournament (from PGA Tour official payouts)
// Used to calculate value picks — includes ALL drafted golfers, not just those in lineups
const GOLFER_EARNINGS = {
  1: { // Genesis Invitational
    'Rory McIlroy': 1800000, 'Ludvig Aberg': 259500, 'Xander Schauffele': 603200,
    'Tommy Fleetwood': 603200, 'Harris English': 224500, 'Viktor Hovland': 78000,
    'Hideki Matsuyama': 136500, 'Collin Morikawa': 603200, 'Russell Henley': 0,
    'J.J. Spaun': 0, 'Shane Lowry': 178250, 'Sepp Straka': 51500,
    'Maverick McNealy': 0, 'Cameron Young': 603200, 'Matt Fitzpatrick': 178250,
    'Patrick Cantlay': 92250, 'Ryan Gerard': 136500, 'Sam Burns': 0,
    'Si Woo Kim': 109000, 'Brooks Koepka': 0, 'Robert MacIntyre': 259500,
    'Aaron Rai': 136500, 'Keegan Bradley': 0, 'Alex Noren': 415000,
    'Jason Day': 0, 'Corey Conners': 92250, 'Ben Griffin': 78000,
    'Justin Thomas': 0, 'Jon Rahm': 0, 'Akshay Bhatia': 319000,
    'Matt McCarty': 178250, 'Min Woo Lee': 415000, 'Brian Harman': 51500,
    'Andrew Novak': 0, 'Pierceson Coody': 319000, 'Marco Penge': 319000,
    'Wyndham Clark': 78000, 'Kurt Kitayama': 1800000, 'Rickie Fowler': 136500,
    'Ryan Fox': 603200, 'Sungjae Im': 0, 'Taylor Pendrith': 64000,
    'Jordan Spieth': 415000, 'Adam Scott': 1400000, 'Jake Knapp': 760000,
    'Nick Taylor': 136500, 'Tony Finau': 136500, 'Sahith Theegala': 224500,
    'Denny McCarthy': 57000, 'Aldrich Potgieter': 840000, 'Sami Valimaki': 92250,
    'Max Homa': 92250, 'Ryo Hisatsune': 64000, 'Max Greyserman': 178250,
    'Nicolai Hojgaard': 0, 'Lucas Glover': 0, 'Daniel Berger': 0,
    'Michael Kim': 0, 'Harry Hall': 0, 'Tom Hoge': 0,
    'Chris Gotterup': 0, 'Joel Dahmen': 0, 'Billy Horschel': 0,
    'Patrick Rodgers': 109000, 'Michael Thorbjornsen': 0, 'Keith Mitchell': 0,
    'Justin Rose': 0, 'Will Zalatoris': 0, 'Tyrrell Hatton': 0,
    'Stephan Jaeger': 0, 'J.T. Poston': 0, 'Patrick Reed': 0,
    'Davis Riley': 0, 'Eric Cole': 0, 'Beau Hossler': 0,
    'Mackenzie Hughes': 0, 'Haotong Li': 0, 'Thomas Detry': 0,
    'Cameron Smith': 0, 'Kristoffer Reitan': 0, 'John Keefer': 0,
    'Rasmus Neergaard-Petersen': 0, 'Rasmus Hojgaard': 0,
    'Jackson Koivun(Am)': 0,
  },
  2: { // Arnold Palmer Invitational
    'Akshay Bhatia': 4000000, 'Daniel Berger': 2200000, 'Ludvig Aberg': 1200000,
    'Cameron Young': 1200000, 'Collin Morikawa': 840000, 'Sahith Theegala': 702000,
    'Russell Henley': 702000, 'Min Woo Lee': 702000, 'Harry Hall': 578000,
    'Rickie Fowler': 578000, 'Adam Scott': 493000, 'Jordan Spieth': 493000,
    'Billy Horschel': 373200, 'Maverick McNealy': 373200, 'Si Woo Kim': 373200,
    'Viktor Hovland': 373200, 'Sepp Straka': 373200, 'Max Greyserman': 261000,
    'Kurt Kitayama': 261000, 'Chris Gotterup': 261000, 'Lucas Glover': 157000,
    'Robert MacIntyre': 157000, 'Nicolai Hojgaard': 157000, 'Ryan Fox': 157000,
    'Patrick Rodgers': 157000, 'Alex Noren': 157000, 'Harris English': 157000,
    'Scottie Scheffler': 157000, 'Xander Schauffele': 157000,
    'Keith Mitchell': 109000, 'Michael Kim': 109000, 'Corey Conners': 109000,
    'Ryo Hisatsune': 109000, 'Michael Thorbjornsen': 109000,
    'Nick Taylor': 90000, 'Taylor Pendrith': 90000, 'Andrew Novak': 90000,
    'Hideki Matsuyama': 78000, 'Matt McCarty': 78000, 'Matt Fitzpatrick': 78000,
    'Tommy Fleetwood': 56000, 'Brian Harman': 54000,
    'Tony Finau': 0, 'Rory McIlroy': 0, 'Shane Lowry': 0,
    'Patrick Cantlay': 0, 'Ryan Gerard': 0, 'Sam Burns': 0,
    'Ben Griffin': 0, 'Aaron Rai': 0, 'Keegan Bradley': 0,
    'Jason Day': 0, 'Wyndham Clark': 0, 'Justin Rose': 0,
    'Marco Penge': 0, 'Pierceson Coody': 0, 'Jake Knapp': 0,
    'Sungjae Im': 0, 'J.J. Spaun': 0, 'Jon Rahm': 0,
    'Denny McCarthy': 0, 'Aldrich Potgieter': 0, 'Sami Valimaki': 0,
    'Max Homa': 0, 'Tom Hoge': 0, 'Joel Dahmen': 0,
    'J.T. Poston': 0, 'Patrick Reed': 0, 'Davis Riley': 0,
    'Eric Cole': 0, 'Beau Hossler': 0, 'Mackenzie Hughes': 0,
    'Haotong Li': 0, 'Thomas Detry': 0, 'Cameron Smith': 0,
    'Kristoffer Reitan': 0, 'John Keefer': 0, 'Tyrrell Hatton': 0,
    'Stephan Jaeger': 0, 'Will Zalatoris': 0, 'Justin Thomas': 0,
    'Rasmus Neergaard-Petersen': 0, 'Rasmus Hojgaard': 0,
    'Jackson Koivun(Am)': 0, 'Brooks Koepka': 0, 'Nick Dunlap': 0,
    'Bryson DeChambeau': 0, 'Tom Kim': 0,
  },
  3: { // The Players Championship
    'Cameron Young': 4500000, 'Matt Fitzpatrick': 2725000, 'Xander Schauffele': 1725000,
    'Robert MacIntyre': 1225000, 'Ludvig Aberg': 925000, 'Tommy Fleetwood': 725000,
    'Sepp Straka': 725000, 'Justin Thomas': 725000, 'Patrick Rodgers': 584375,
    'Brian Harman': 584375, 'Sam Burns': 377778, 'Akshay Bhatia': 377778,
    'Brooks Koepka': 377778, 'Ryo Hisatsune': 377778, 'Justin Rose': 377778,
    'Russell Henley': 377778, 'Viktor Hovland': 377778, 'Corey Conners': 377778,
    'Scottie Scheffler': 255625, 'Michael Thorbjornsen': 255625,
    'J.J. Spaun': 210000, 'Alex Smalley': 210000,
    'Hideki Matsuyama': 158000, 'Nicolai Hojgaard': 158000, 'Chris Kirk': 158000,
    'Ryan Gerard': 158000, 'Eric Cole': 100812, 'Jordan Spieth': 100812,
    'Patrick Cantlay': 100812, 'Andrew Putnam': 100812, 'Alex Noren': 100812,
    'Max Homa': 100812, 'Min Woo Lee': 100812, 'Maverick McNealy': 100812,
    'Sahith Theegala': 100812, 'Rickie Fowler': 65625, 'Nick Taylor': 65625,
    'Wyndham Clark': 65625, 'Rory McIlroy': 54062, 'Taylor Pendrith': 54062,
    'Keith Mitchell': 54062, 'J.T. Poston': 48500, 'Si Woo Kim': 48500,
    'Keegan Bradley': 48500, 'Adam Scott': 46875, 'Chris Gotterup': 46875,
    'Jason Day': 46000, 'Lee Hodges': 46000, 'Stephan Jaeger': 45125,
    'Kristoffer Reitan': 44250, 'Daniel Berger': 44250, 'Nico Echavarria': 44250,
    'Tony Finau': 43250,
    'Harris English': 0, 'Aaron Rai': 0, 'Ben Griffin': 0, 'Ryan Fox': 0,
    'Rasmus Hojgaard': 0, 'Max Greyserman': 0, 'Rasmus Neergaard-Petersen': 0,
    'Kurt Kitayama': 0, 'Jon Rahm': 0, 'Haotong Li': 0,
    'Collin Morikawa': 0, 'Billy Horschel': 0, 'Marco Penge': 0,
    'Harry Hall': 0, 'John Keefer': 0, 'Sami Valimaki': 0,
    'Pierceson Coody': 0, 'Tom Hoge': 0, 'Jackson Koivun(Am)': 0,
    'Shane Lowry': 0, 'Jake Knapp': 0, 'Andrew Novak': 0,
    'Denny McCarthy': 0, 'Will Zalatoris': 0, 'Sungjae Im': 0,
    'Aldrich Potgieter': 0, 'Cameron Smith': 0, 'Tyrrell Hatton': 0,
    'Patrick Reed': 0, 'Thomas Detry': 0, 'Davis Riley': 0,
    'Joel Dahmen': 0, 'Beau Hossler': 0, 'Lucas Glover': 0,
    'Mackenzie Hughes': 0, 'Michael Kim': 0, 'Bryson DeChambeau': 0,
    'Matt McCarty': 0,
  },
  4: { // The Masters
    'Rory McIlroy': 4500000, 'Scottie Scheffler': 2430000,
    'Tyrrell Hatton': 1080000, 'Russell Henley': 1080000, 'Justin Rose': 1080000, 'Cameron Young': 1080000,
    'Collin Morikawa': 725625, 'Sam Burns': 725625,
    'Max Homa': 630000, 'Xander Schauffele': 630000,
    'Jake Knapp': 562500,
    'Jordan Spieth': 427500, 'Hideki Matsuyama': 427500, 'Brooks Koepka': 427500,
    'Patrick Cantlay': 427500, 'Patrick Reed': 427500, 'Jason Day': 427500,
    'Maverick McNealy': 315000, 'Viktor Hovland': 315000, 'Matt Fitzpatrick': 315000,
    'Keegan Bradley': 252000, 'Ludvig Aberg': 252000, 'Wyndham Clark': 252000,
    'Matt McCarty': 182250, 'Adam Scott': 182250, 'Chris Gotterup': 182250,
    'Alex Noren': 146250, 'Harris English': 146250, 'Shane Lowry': 146250,
    'Tommy Fleetwood': 121500, 'Ben Griffin': 121500,
    'Jon Rahm': 101250, 'Ryan Gerard': 101250,
    'Justin Thomas': 83250, 'Sepp Straka': 83250, 'Nick Taylor': 83250,
    'Sungjae Im': 69750, 'Si Woo Kim': 65250, 'Aaron Rai': 61650,
    'Corey Conners': 57600, 'Marco Penge': 57600,
    'Kurt Kitayama': 55350, 'Rasmus Hojgaard': 53100,
    'Ryan Fox': 0, 'Robert MacIntyre': 0, 'Bryson DeChambeau': 0,
    'Nicolai Hojgaard': 0, 'Akshay Bhatia': 0, 'Tony Finau': 0,
    'Harry Hall': 0, 'J.J. Spaun': 0, 'Min Woo Lee': 0,
    'Will Zalatoris': 0, 'Cameron Smith': 0, 'Pierceson Coody': 0,
    'Denny McCarthy': 0, 'Taylor Pendrith': 0, 'Ryo Hisatsune': 0,
    'Aldrich Potgieter': 0, 'Sami Valimaki': 0, 'Max Greyserman': 0,
    'Davis Riley': 0, 'Andrew Novak': 0, 'Michael Kim': 0,
    'Daniel Berger': 0, 'Rasmus Neergaard-Petersen': 0,
    'Stephan Jaeger': 0, 'Rickie Fowler': 0, 'Nick Dunlap': 0,
    'Sahith Theegala': 0, 'Tom Hoge': 0, 'Joel Dahmen': 0,
    'Billy Horschel': 0, 'Lucas Glover': 0, 'Eric Cole': 0,
    'Beau Hossler': 0, 'Mackenzie Hughes': 0, 'Haotong Li': 0,
    'Thomas Detry': 0, 'Patrick Rodgers': 0, 'Keith Mitchell': 0,
    'Michael Thorbjornsen': 0, 'J.T. Poston': 0, 'Brian Harman': 0,
    'Kristoffer Reitan': 0, 'John Keefer': 0, 'Jackson Koivun(Am)': 0,
  }
};

// Build participants from SEASON_ROSTERS
const PARTICIPANT_NAMES = [
  'Stack', 'Drozd', 'Jedlicka', 'Chin', 'Hoffmann', "O'Hare",
  'Starr', 'Ben', 'McEldowney', 'Noonan', 'Moy', 'Stumpf'
];

const INITIAL_PARTICIPANTS = PARTICIPANT_NAMES.map((name, index) => {
  const roster = SEASON_ROSTERS[name] || [];
  const draftedGolferIds = roster.map(golferName => {
    const golfer = INITIAL_GOLFERS.find(g => g.name === golferName);
    return golfer ? golfer.id : null;
  }).filter(Boolean);
  return {
    id: index + 1,
    name,
    totalWinnings: 0,
    majorWinnings: 0,
    draftedGolfers: draftedGolferIds,
    weeklyLineups: HARDCODED_LINEUPS[name] || {}
  };
});

// Compute earnings from tournamentHistory
const computeEarnings = (participantName, history) => {
  let totalWinnings = 0;
  let majorWinnings = 0;
  Object.values(history).forEach(tournamentData => {
    const pData = tournamentData[participantName];
    if (pData) {
      totalWinnings += pData.earnings || 0;
      majorWinnings += pData.majorEarnings || 0;
    }
  });
  return { totalWinnings, majorWinnings };
};

// Determine tournament status based on date
const getTournamentStatus = (tournament) => {
  if (tournament.completed) return 'completed';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Parse date parts to avoid UTC timezone offset issues
  const [y, m, d] = tournament.date.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 4); // Thu-Sun = 4 days, transition to concluded on Monday
  if (today >= start && today < end) return 'in-progress';
  if (today >= end) return 'concluded';
  return 'upcoming';
};

// Avatar color map for participant initials
const PARTICIPANT_COLORS = {
  'Stack': '#e74c3c', 'Drozd': '#3498db', 'Jedlicka': '#2ecc71', 'Chin': '#9b59b6',
  'Hoffmann': '#f39c12', "O'Hare": '#1abc9c', 'Starr': '#e67e22', 'Ben': '#16a085',
  'McEldowney': '#8e44ad', 'Noonan': '#d35400', 'Moy': '#27ae60', 'Stumpf': '#c0392b'
};

// Avatar photos — maps participant name to image path (add more as photos are uploaded)
const PARTICIPANT_AVATARS = {
  "O'Hare": '/images/avatars/ohare.jpg',
  'Stack': '/images/avatars/stack.jpg',
  'Ben': '/images/avatars/ben.jpg',
  'Starr': '/images/avatars/starr.jpg',
  'McEldowney': '/images/avatars/mceldowney.jpg',
  'Stumpf': '/images/avatars/stumpf.jpg',
  'Drozd': '/images/avatars/drozd.jpg',
  'Moy': '/images/avatars/moy.jpg',
  'Jedlicka': '/images/avatars/jedlicka.png',
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Reusable avatar component — shows photo if available, falls back to colored initials
// Hovering over a photo avatar shows an enlarged preview positioned via fixed coordinates
const AvatarCircle = ({ name, size = 30, className = 'avatar-circle' }) => {
  const avatar = PARTICIPANT_AVATARS[name];
  const color = PARTICIPANT_COLORS[name] || '#d4af37';
  const [hoverPos, setHoverPos] = useState(null);

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Position to the right of the avatar, vertically centered
    setHoverPos({ top: rect.top - 60, left: rect.right + 12 });
  };
  const handleMouseLeave = () => setHoverPos(null);

  if (avatar) {
    return (
      <div className={`${className} avatar-has-photo`}
        style={{ background: 'transparent', overflow: 'hidden', width: size, height: size, minWidth: size, minHeight: size }}
        onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        {hoverPos && (
          <div className="avatar-hover-preview" style={{ display: 'block', top: hoverPos.top, left: hoverPos.left }}>
            <img src={avatar} alt={name} />
            <div className="avatar-hover-name">{name}</div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={className} style={{ background: color, width: size, height: size, minWidth: size, minHeight: size }}>
      {getInitials(name)}
    </div>
  );
};

// ==================== WEEKLY RECAP MODAL ====================
const WeeklyRecap = ({ tournamentId, tournamentHistory, onClose }) => {
  const tournament = TOURNAMENTS.find(t => t.id === tournamentId);
  const [expandedTeam, setExpandedTeam] = useState(null);
  if (!tournament) return null;

  const tournamentData = tournamentHistory[tournamentId];
  if (!tournamentData) return null;

  // Get earnings for this tournament, sorted highest to lowest
  const earnings = Object.entries(tournamentData)
    .map(([name, data]) => ({ name, earnings: data.earnings || 0, lineup: data.lineup || [] }))
    .sort((a, b) => b.earnings - a.earnings);

  const biggestEarner = earnings[0];

  // Get individual golfer earnings for this tournament
  const golferEarningsForTournament = GOLFER_EARNINGS[tournamentId] || {};

  // Compute standings before and after this tournament
  const getStandingsBefore = () => {
    const historyBefore = {};
    Object.keys(tournamentHistory).forEach(tid => {
      if (parseInt(tid) < tournamentId) historyBefore[tid] = tournamentHistory[tid];
    });
    return PARTICIPANT_NAMES
      .map(name => ({ name, total: computeEarnings(name, historyBefore).totalWinnings }))
      .sort((a, b) => b.total - a.total);
  };

  const getStandingsAfter = () => {
    const historyAfter = {};
    Object.keys(tournamentHistory).forEach(tid => {
      if (parseInt(tid) <= tournamentId) historyAfter[tid] = tournamentHistory[tid];
    });
    return PARTICIPANT_NAMES
      .map(name => ({ name, total: computeEarnings(name, historyAfter).totalWinnings }))
      .sort((a, b) => b.total - a.total);
  };

  const before = getStandingsBefore();
  const after = getStandingsAfter();

  const getMovement = (name) => {
    const rankBefore = before.findIndex(p => p.name === name) + 1;
    const rankAfter = after.findIndex(p => p.name === name) + 1;
    return rankBefore - rankAfter; // positive = moved up
  };

  return (
    <div className="recap-overlay" onClick={onClose}>
      <div className="recap-modal" onClick={e => e.stopPropagation()}>
        <button className="recap-close" onClick={onClose}>&times;</button>
        <h2 className="recap-title">{tournament.name} Recap</h2>

        {biggestEarner && (
          <div className="recap-biggest">
            <div className="recap-biggest-label">Biggest Earner</div>
            <AvatarCircle name={biggestEarner.name} size={56} className="recap-biggest-avatar" />
            <div className="recap-biggest-name">{biggestEarner.name}</div>
            <div className="recap-biggest-amount">{formatCurrency(biggestEarner.earnings)}</div>
          </div>
        )}

        <div className="recap-earnings-list">
          {earnings.map((entry, idx) => {
            const movement = getMovement(entry.name);
            const isExpanded = expandedTeam === entry.name;
            return (
              <div key={entry.name}>
                <div className={`recap-earnings-row ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => setExpandedTeam(isExpanded ? null : entry.name)}
                  style={{ cursor: 'pointer' }}>
                  <span className="recap-rank">#{idx + 1}</span>
                  <AvatarCircle name={entry.name} size={28} className="recap-avatar-small" />
                  <span className="recap-name">{entry.name}</span>
                  <span className="recap-expand-icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                  <span className="recap-amount">{formatCurrency(entry.earnings)}</span>
                  <span className={`recap-movement ${movement > 0 ? 'up' : movement < 0 ? 'down' : ''}`}>
                    {movement > 0 ? `\u25B2${movement}` : movement < 0 ? `\u25BC${Math.abs(movement)}` : '\u2014'}
                  </span>
                </div>
                {isExpanded && entry.lineup.length > 0 && (
                  <div className="recap-lineup-detail">
                    {entry.lineup.map((golferName, gi) => {
                      const golferEarned = golferEarningsForTournament[golferName] || 0;
                      return (
                        <div key={gi} className="recap-golfer-row">
                          <span className="recap-golfer-name">{golferName}</span>
                          <span className={`recap-golfer-earnings ${golferEarned > 0 ? 'earned' : 'zero'}`}>
                            {golferEarned > 0 ? formatCurrency(golferEarned) : 'MC'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const FantasyGolfLeague = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem('sp_auth') === 'true');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => sessionStorage.getItem('sp_admin') === 'true');

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === '1007') {
      sessionStorage.setItem('sp_auth', 'true');
      setIsAuthenticated(true);
      setPasswordError(false);
    } else if (passwordInput === 'HaleyBoobs') {
      sessionStorage.setItem('sp_auth', 'true');
      sessionStorage.setItem('sp_admin', 'true');
      setIsAuthenticated(true);
      setIsAdmin(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };

  const [currentPage, setCurrentPage] = useState('home');
  const [participants, setParticipants] = useState(INITIAL_PARTICIPANTS);
  const [golfers] = useState(INITIAL_GOLFERS);
  const [tournamentHistory, setTournamentHistory] = useState(HARDCODED_HISTORY);
  const [showRecap, setShowRecap] = useState(null);

  // Compute earnings from tournamentHistory
  const participantsWithEarnings = useMemo(() => {
    return participants.map(p => {
      const earnings = computeEarnings(p.name, tournamentHistory);
      return {
        ...p,
        totalWinnings: earnings.totalWinnings,
        majorWinnings: earnings.majorWinnings
      };
    });
  }, [participants, tournamentHistory]);

  // Load data from Firebase on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading data from Firebase...');
        const firebaseData = await firebaseService.loadAllData();

        // Merge weeklyLineups from Firebase, but hardcoded lineups take priority
        if (firebaseData.participants) {
          setParticipants(prev => prev.map(p => {
            const saved = firebaseData.participants.find(sp => sp.name === p.name);
            if (saved && saved.weeklyLineups) {
              const hardcoded = HARDCODED_LINEUPS[p.name] || {};
              return { ...p, weeklyLineups: { ...saved.weeklyLineups, ...hardcoded } };
            }
            return p;
          }));
          console.log('Weekly lineups merged from Firebase');
        }

        // Load tournament history, but hardcoded data takes priority for completed tournaments
        if (firebaseData.tournamentHistory) {
          setTournamentHistory(prev => {
            const merged = { ...firebaseData.tournamentHistory };
            // Hardcoded history always wins for completed tournaments
            Object.keys(HARDCODED_HISTORY).forEach(key => {
              merged[key] = HARDCODED_HISTORY[key];
            });
            return merged;
          });
          console.log('Tournament history merged from Firebase');
        }
      } catch (error) {
        console.error('Error loading from Firebase:', error.message);
      }
    };
    loadData();
  }, []);

  // Auto-save with 500ms debounce
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      const saveData = async () => {
        try {
          await firebaseService.saveAllData({
            participants,
            golfers,
            tournamentHistory
          });
          console.log('Data auto-saved to Firebase');
        } catch (error) {
          console.error('Error auto-saving:', error);
          try {
            localStorage.setItem('participants', JSON.stringify(participants));
            localStorage.setItem('tournamentHistory', JSON.stringify(tournamentHistory));
            console.log('Saved to localStorage as fallback');
          } catch (e) {
            console.error('Fallback save failed:', e);
          }
        }
      };
      saveData();
    }, 500);
    return () => clearTimeout(saveTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants, tournamentHistory]);

  // ==================== HOME PAGE ====================
  const HomePage = () => {
    const sortedParticipants = [...participantsWithEarnings].sort((a, b) => b.totalWinnings - a.totalWinnings);
    const [expandedParticipant, setExpandedParticipant] = useState(null);
    const [leaderboardData, setLeaderboardData] = useState({ tournament: null, players: {} });

    const toggleParticipant = (participantId) => {
      setExpandedParticipant(expandedParticipant === participantId ? null : participantId);
    };

    const hasInProgressTournament = TOURNAMENTS.some(t => getTournamentStatus(t) === 'in-progress');

    // Fetch live leaderboard data when a tournament is in progress
    useEffect(() => {
      if (!hasInProgressTournament) return;

      const fetchLeaderboard = async () => {
        const data = await dataGolfService.getLeaderboard();
        setLeaderboardData(data);
      };
      fetchLeaderboard();

      // Auto-refresh every 2 minutes during in-progress tournaments
      const interval = setInterval(fetchLeaderboard, 2 * 60 * 1000);
      return () => clearInterval(interval);
    }, [hasInProgressTournament]);

    // Splash screen state
    const [showSplash, setShowSplash] = useState(true);
    useEffect(() => {
      const timer = setTimeout(() => setShowSplash(false), 8000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <div className="home-page">
        {showSplash && (
          <div className="splash-overlay" onClick={() => setShowSplash(false)}>
            <div className="splash-content">
              <img src="/images/hero-banner.png" alt="Welcome to the Swimming Pool" className="splash-image" />
              <div className="splash-skip-hint">Click anywhere to skip</div>
            </div>
          </div>
        )}

        <div className="home-layout">
          <div className="home-left">
            <div className="leaderboard-section">
              <h2 className="section-title">Season Standings</h2>
              <div className="leaderboard">
                <div className="leaderboard-header">
                  <div className="rank-col">Rank</div>
                  <div className="name-col">Player</div>
                  <div className="winnings-col">Total Winnings</div>
                </div>
                {(() => {
                  // Compute previous standings (before the most recent completed tournament)
                  const completedTournamentIds = Object.keys(tournamentHistory)
                    .map(Number)
                    .sort((a, b) => a - b);
                  const lastCompletedId = completedTournamentIds[completedTournamentIds.length - 1];
                  const previousHistory = {};
                  completedTournamentIds.forEach(tid => {
                    if (tid < lastCompletedId) previousHistory[tid] = tournamentHistory[tid];
                  });
                  const previousRanks = {};
                  if (Object.keys(previousHistory).length > 0) {
                    const prevStandings = PARTICIPANT_NAMES
                      .map(name => ({ name, total: computeEarnings(name, previousHistory).totalWinnings }))
                      .sort((a, b) => b.total - a.total);
                    prevStandings.forEach((p, i) => { previousRanks[p.name] = i + 1; });
                  }
                  return sortedParticipants.map((participant, index) => {
                  const currentRank = index + 1;
                  const prevRank = previousRanks[participant.name];
                  const movement = prevRank ? prevRank - currentRank : 0;
                  const isExpanded = expandedParticipant === participant.id;
                  const draftedGolferObjects = participant.draftedGolfers
                    .map(golferId => golfers.find(g => g.id === golferId))
                    .filter(g => g);

                  const nextTournamentForLineup = TOURNAMENTS.find(t => {
                    const status = getTournamentStatus(t);
                    return status === 'upcoming' || status === 'in-progress';
                  });
                  const currentLineup = nextTournamentForLineup?.id
                    ? participant.weeklyLineups[nextTournamentForLineup.id] || []
                    : [];
                  const lineupGolfers = currentLineup
                    .map(golferId => golfers.find(g => g.id === golferId))
                    .filter(g => g);
                  const benchGolfers = draftedGolferObjects.filter(
                    golfer => !currentLineup.includes(golfer.id)
                  );

                  return (
                    <div key={participant.id}>
                      <div
                        className={`leaderboard-row ${index === 0 ? 'leader' : ''} ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleParticipant(participant.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="rank-col">
                          {index === 0 ? <Trophy size={20} /> : `#${index + 1}`}
                        </div>
                        <div className="name-col">
                          <AvatarCircle name={participant.name} size={30} className="avatar-circle" />
                          {participant.name}
                          <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                            {isExpanded ? '\u25BC' : '\u25B6'}
                          </span>
                        </div>
                        <div className="winnings-col">
                          {formatCurrency(participant.totalWinnings)}
                          {movement !== 0 && (
                            <span className={`leaderboard-movement ${movement > 0 ? 'up' : 'down'}`}>
                              {movement > 0 ? `\u25B2${movement}` : `\u25BC${Math.abs(movement)}`}
                            </span>
                          )}
                          {movement === 0 && prevRank && (
                            <span className="leaderboard-movement flat">{'\u2014'}</span>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="participant-details">
                          <div className="details-section">
                            <h3>Current Lineup - {nextTournamentForLineup?.name || 'No Active Tournament'}</h3>
                            {lineupGolfers.length > 0 ? (
                              <div className="golfer-list">
                                {(() => {
                                  const tournamentIsLive = hasInProgressTournament && nextTournamentForLineup && getTournamentStatus(nextTournamentForLineup) === 'in-progress';
                                  return tournamentIsLive ? (
                                    <>
                                      <div className="golfer-list-header">
                                        <span className="golfer-header-name">Player</span>
                                        <span className="golfer-header-pos">Pos</span>
                                        <span className="golfer-header-score">Score</span>
                                        <span className="golfer-header-thru">Thru</span>
                                      </div>
                                      {lineupGolfers.map(golfer => {
                                        const lbEntry = leaderboardData.players[normalizeName(golfer.name)];
                                        return (
                                          <div key={golfer.id} className="golfer-item lineup-item has-live-data">
                                            <span className="golfer-name">{golfer.name}</span>
                                            <span className="golfer-pos-val">{lbEntry?.position || '-'}</span>
                                            <span className="golfer-score-val">{lbEntry?.score || '-'}</span>
                                            <span className="golfer-thru-val">{lbEntry?.thru || '-'}</span>
                                          </div>
                                        );
                                      })}
                                    </>
                                  ) : (
                                    lineupGolfers.map(golfer => (
                                      <div key={golfer.id} className="golfer-item lineup-item">
                                        <span className="golfer-rank">#{golfer.owgr}</span>
                                        <span className="golfer-name">{golfer.name}</span>
                                      </div>
                                    ))
                                  );
                                })()}
                              </div>
                            ) : (
                              <p className="no-data">No lineup set for this tournament</p>
                            )}
                          </div>
                          <div className="details-section">
                            <h3>Bench Players ({benchGolfers.length})</h3>
                            {benchGolfers.length > 0 ? (
                              <div className="golfer-list">
                                {(() => {
                                  const hasLiveData = hasInProgressTournament && Object.keys(leaderboardData.players || {}).length > 0;
                                  return hasLiveData ? (
                                    <>
                                      <div className="golfer-list-header">
                                        <span className="golfer-header-name">Player</span>
                                        <span className="golfer-header-pos">Pos</span>
                                        <span className="golfer-header-score">Score</span>
                                        <span className="golfer-header-thru">Thru</span>
                                      </div>
                                      {benchGolfers.map(golfer => {
                                        const lbEntry = leaderboardData.players[normalizeName(golfer.name)];
                                        return (
                                          <div key={golfer.id} className="golfer-item bench-item has-live-data">
                                            <span className="golfer-name">{golfer.name}</span>
                                            <span className="golfer-pos-val">{lbEntry?.position || 'N/A'}</span>
                                            <span className="golfer-score-val">{lbEntry?.score || 'N/A'}</span>
                                            <span className="golfer-thru-val">{lbEntry?.thru || 'N/A'}</span>
                                          </div>
                                        );
                                      })}
                                    </>
                                  ) : (
                                    benchGolfers.map(golfer => (
                                      <div key={golfer.id} className="golfer-item bench-item">
                                        <span className="golfer-rank">#{golfer.owgr}</span>
                                        <span className="golfer-name">{golfer.name}</span>
                                      </div>
                                    ))
                                  );
                                })()}
                              </div>
                            ) : (
                              <p className="no-data">
                                {draftedGolferObjects.length === 0
                                  ? 'No golfers drafted yet'
                                  : 'All drafted golfers are in the lineup'}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
                })()}
              </div>
            </div>
          </div>

          <div className="home-center">
            <div className="stat-card leader-card">
              <div className="stat-header"><Medal className="stat-icon" /><h3>Current Leader</h3></div>
              <div className="leader-info">
                <div className="leader-name">{sortedParticipants[0]?.name}</div>
                <div className="leader-winnings">{formatCurrency(sortedParticipants[0]?.totalWinnings)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-header"><Trophy className="stat-icon" size={18} /><h3>Season Overview</h3></div>
              <div className="insight-grid">
                <div className="insight-item">
                  <div className="insight-value">{formatCurrency(sortedParticipants.reduce((sum, p) => sum + p.totalWinnings, 0))}</div>
                  <div className="insight-label">Total Prize Pool</div>
                </div>
                <div className="insight-item">
                  <div className="insight-value">{TOURNAMENTS.filter(t => getTournamentStatus(t) === 'completed' || getTournamentStatus(t) === 'concluded').length} / {TOURNAMENTS.length}</div>
                  <div className="insight-label">Tournaments Played</div>
                </div>
              </div>
            </div>
            {(() => {
              let biggestWeek = { name: '', tournament: '', amount: 0 };
              Object.entries(tournamentHistory).forEach(([tid, tData]) => {
                const tournament = TOURNAMENTS.find(t => t.id === parseInt(tid));
                Object.entries(tData).forEach(([name, data]) => {
                  if ((data.earnings || 0) > biggestWeek.amount) {
                    biggestWeek = { name, tournament: tournament?.name || '', amount: data.earnings };
                  }
                });
              });
              if (biggestWeek.amount > 0) {
                return (
                  <div className="stat-card">
                    <div className="stat-header"><h3>Biggest Week</h3></div>
                    <div className="biggest-week">
                      <AvatarCircle name={biggestWeek.name} size={40} className="biggest-week-avatar" />
                      <div className="biggest-week-name">{biggestWeek.name}</div>
                      <div className="biggest-week-amount">{formatCurrency(biggestWeek.amount)}</div>
                      <div className="biggest-week-tournament">{biggestWeek.tournament}</div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            {(() => {
              // Compute Worst 1st Round Pick: first round player with lowest cumulative earnings
              const firstRoundStats = Object.entries(FIRST_ROUND_PICKS).map(([golferName, info]) => {
                let totalEarnings = 0;
                Object.entries(GOLFER_EARNINGS).forEach(([, tEarnings]) => {
                  totalEarnings += tEarnings[golferName] || 0;
                });
                return { name: golferName, owner: info.owner, pick: info.pick, earnings: totalEarnings };
              }).sort((a, b) => a.earnings - b.earnings);
              const worst = firstRoundStats[0];
              if (worst) {
                return (
                  <div className="stat-card">
                    <div className="stat-header"><h3>Worst 1st Round Pick</h3></div>
                    <div className="biggest-week">
                      <AvatarCircle name={worst.owner} size={40} className="biggest-week-avatar" />
                      <div className="biggest-week-name">{worst.name}</div>
                      <div className="biggest-week-amount">{formatCurrency(worst.earnings)}</div>
                      <div className="biggest-week-tournament">Pick #{worst.pick} · {worst.owner}</div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            {(() => {
              // Compute Top Value Picks: rounds 5-7 players ranked by cumulative individual earnings
              const valuePicks = Object.entries(LATE_ROUND_PICKS).map(([golferName, info]) => {
                let totalEarnings = 0;
                Object.entries(GOLFER_EARNINGS).forEach(([, tEarnings]) => {
                  totalEarnings += tEarnings[golferName] || 0;
                });
                return { name: golferName, owner: info.owner, round: info.round, pick: info.pick, earnings: totalEarnings };
              }).sort((a, b) => b.earnings - a.earnings);
              const topPicks = valuePicks.filter(p => p.earnings > 0).slice(0, 8);
              return (
                <div className="stat-card value-picks-card">
                  <div className="stat-header"><h3>Top Value Picks</h3></div>
                  <div className="value-picks-subtitle">Rounds 5-7 players ranked by earnings</div>
                  <div className="value-picks-list">
                    {topPicks.map((pick, idx) => (
                      <div key={pick.name} className="value-pick-row">
                        <span className="value-pick-rank">#{idx + 1}</span>
                        <div className="value-pick-info">
                          <div className="value-pick-name">{pick.name}</div>
                          <div className="value-pick-meta">Rd {pick.round} · Pick #{pick.pick} · {pick.owner}</div>
                        </div>
                        <span className="value-pick-earnings">{formatCurrency(pick.earnings)}</span>
                      </div>
                    ))}
                    {topPicks.length === 0 && (
                      <div className="no-data">No earnings yet for late-round picks</div>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="stat-card coming-soon-card">
              <div className="stat-header"><h3>Coming Soon</h3></div>
              <div className="coming-soon-content">
                <div className="coming-soon-item">Player Trends</div>
              </div>
            </div>
          </div>

          <div className="home-right">
            <div className="tournament-schedule">
              <h2 className="section-title">Tournament Schedule</h2>
              <div className="tournament-list">
                {TOURNAMENTS.map(tournament => {
                  const status = getTournamentStatus(tournament);
                  return (
                    <div key={tournament.id} className={`tournament-card ${tournament.type} ${status === 'completed' || status === 'concluded' ? 'completed' : ''}`}>
                      <div className="tournament-type-badge">{tournament.type}</div>
                      <div className="tournament-name">{tournament.name}</div>
                      <div className="tournament-course">{tournament.course}</div>
                      <div className="tournament-location">{'\ud83d\udccd'} {tournament.location}</div>
                      <div className="tournament-specs">
                        <span>Par {tournament.par}</span>
                        <span className="spec-separator">{'\u2022'}</span>
                        <span>{tournament.yardage.toLocaleString()} yards</span>
                      </div>
                      <div className="tournament-date">{new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      {status === 'completed' && (<div className="status-pill completed">{'\u2713'} Complete</div>)}
                      {status === 'in-progress' && (<div className="status-pill live"><span className="pulse-dot"></span> Live</div>)}
                      {status === 'concluded' && (<div className="status-pill completed">Concluded</div>)}
                      {(status === 'completed' || status === 'concluded') && tournamentHistory[tournament.id] && (
                        <button className="recap-btn" onClick={() => setShowRecap(tournament.id)}>View Recap</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== DRAFT PAGE (READ-ONLY) ====================
  const DraftPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const undraftedGolfers = golfers.filter(g => !g.drafted);
    const filteredUndrafted = undraftedGolfers.filter(g =>
      g.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="draft-page">
        <div className="draft-header">
          <h1 className="page-title">Draft Board</h1>
          <p className="draft-info">Season rosters are locked. View team rosters below.</p>
        </div>

        <div className="team-rosters-section">
          <h2 className="section-title-large">Team Rosters</h2>
          <div className="teams-grid-wide">
            {participantsWithEarnings.map(participant => (
              <div key={participant.id} className="team-card-compact">
                <div className="team-header-compact">
                  <span className="team-name-compact">{participant.name}</span>
                  <span className="team-count-badge">{participant.draftedGolfers.length}/7</span>
                </div>
                <div className="team-roster-compact">
                  {participant.draftedGolfers.map(golferId => {
                    const golfer = golfers.find(g => g.id === golferId);
                    return (
                      <div key={golferId} className="roster-golfer-compact">
                        <div className="golfer-info-compact">
                          <span className="golfer-rank-small">#{golfer?.owgr}</span>
                          <span className="golfer-name-small">{golfer?.name}</span>
                        </div>
                      </div>
                    );
                  })}
                  {participant.draftedGolfers.length === 0 && (
                    <div className="roster-empty-compact">No golfers yet</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="available-golfers-section">
          <h2 className="section-title-large">Undrafted Golfers</h2>
          <div className="search-box">
            <input type="text" placeholder="Search golfers by name..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
            <span className="golfer-count">{filteredUndrafted.length} available</span>
          </div>
          <div className="golfers-grid-wide">
            {filteredUndrafted.map(golfer => {
              let tierClass = '';
              if (golfer.owgr <= 50) tierClass = 'top-50';
              else if (golfer.owgr <= 70) tierClass = 'top-70';
              return (
                <div key={golfer.id} className={`golfer-card-wide ${tierClass}`}>
                  <div className="golfer-rank-badge-wide">#{golfer.owgr}</div>
                  <div className="golfer-name-wide">{golfer.name}</div>
                  <div className="golfer-status-wide"><span className="available-label">Available</span></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ==================== LINEUP PAGE (MULTI-TOURNAMENT) ====================
  const LineupPage = () => {
    const [selectedParticipant, setSelectedParticipant] = useState(participantsWithEarnings[0]);
    const [selectedTournamentId, setSelectedTournamentId] = useState(TOURNAMENTS[0].id);
    const [selectedLineup, setSelectedLineup] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [sgStats, setSgStats] = useState({});
    const [fedexRankings, setFedexRankings] = useState({});
    const [liveOwgr, setLiveOwgr] = useState({});

    const selectedTournament = TOURNAMENTS.find(t => t.id === selectedTournamentId);
    const tournamentStatus = selectedTournament ? getTournamentStatus(selectedTournament) : 'upcoming';
    const isLocked = (tournamentStatus === 'in-progress' || tournamentStatus === 'completed' || tournamentStatus === 'concluded') && !isAdmin;

    const participantGolfers = selectedParticipant.draftedGolfers.map(id =>
      golfers.find(g => g.id === id)
    ).filter(Boolean);

    // Helper to get current OWGR (live if available, else hardcoded)
    const getOwgr = (golfer) => liveOwgr[normalizeName(golfer.name)] || golfer.owgr;
    const getFedex = (golfer) => fedexRankings[normalizeName(golfer.name)] || null;

    useEffect(() => {
      const fetchRankings = async () => {
        const [fedex, owgr] = await Promise.all([
          dataGolfService.getFedExCupRankings(),
          dataGolfService.getOWGRRankingsLive()
        ]);
        setFedexRankings(fedex);
        setLiveOwgr(owgr);
      };
      fetchRankings();
    }, []);

    useEffect(() => {
      const fetchSgStats = async () => {
        const stats = await dataGolfService.getSkillData();
        setSgStats(stats);
      };
      fetchSgStats();
    }, []);

    useEffect(() => {
      const lineup = selectedParticipant.weeklyLineups?.[selectedTournamentId] || [];
      setSelectedLineup(lineup);
      setEditMode(false);
    }, [selectedParticipant, selectedTournamentId]);

    const toggleGolferInLineup = (golferId) => {
      if (isLocked) return;
      if (selectedLineup.includes(golferId)) {
        setSelectedLineup(selectedLineup.filter(id => id !== golferId));
      } else if (selectedLineup.length < 4) {
        setSelectedLineup([...selectedLineup, golferId]);
      }
    };

    const saveLineup = async () => {
      const updatedParticipants = participants.map(p =>
        p.id === selectedParticipant.id
          ? { ...p, weeklyLineups: { ...p.weeklyLineups, [selectedTournamentId]: selectedLineup } }
          : p
      );
      setParticipants(updatedParticipants);
      setEditMode(false);
      try {
        await firebaseService.saveAllData({ participants: updatedParticipants, golfers, tournamentHistory });
        console.log('Lineup saved to Firebase');
      } catch (error) {
        console.error('Error saving lineup:', error);
        try { localStorage.setItem('participants', JSON.stringify(updatedParticipants)); } catch (e) {}
      }
    };

    const getTabName = (tournament) => {
      const abbrevs = {
        'Genesis Invitational': 'Genesis',
        'Arnold Palmer Invitational': 'API',
        'The Players Championship': 'TPC',
        'The Masters': 'Masters',
        'RBC Heritage': 'RBC',
        'PGA Championship': 'PGA',
        'The Memorial': 'Memorial',
        'U.S. Open': 'US Open',
        'The Open Championship': 'The Open',
        'BMW Championship': 'BMW'
      };
      return abbrevs[tournament.name] || tournament.name;
    };

    return (
      <div className="lineup-page">
        <div className="lineup-header">
          <h1 className="page-title">Lineup</h1>
        </div>

        <div className="participant-selector">
          <label>Select Participant:</label>
          <select value={selectedParticipant.id}
            onChange={(e) => {
              const participant = participantsWithEarnings.find(p => p.id === parseInt(e.target.value));
              setSelectedParticipant(participant);
            }} className="participant-select">
            {participantsWithEarnings.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="tournament-tabs">
          {TOURNAMENTS.map(tournament => {
            const status = getTournamentStatus(tournament);
            const isActive = tournament.id === selectedTournamentId;
            const locked = status === 'in-progress' || status === 'completed' || status === 'concluded';
            return (
              <button key={tournament.id}
                className={`tournament-tab ${isActive ? 'active' : ''} ${status}`}
                onClick={() => setSelectedTournamentId(tournament.id)}>
                {locked && <span className="tab-lock-icon"><Lock size={12} /></span>}
                <span className="tab-name">{getTabName(tournament)}</span>
                <span className="tab-date">{new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                {status === 'in-progress' && <span className="tab-status-dot live"></span>}
                {(status === 'completed' || status === 'concluded') && <span className="tab-status-dot done"></span>}
              </button>
            );
          })}
        </div>

        {isLocked && (
          <div className="lock-banner">
            <Lock size={20} />
            <span>
              {tournamentStatus === 'in-progress'
                ? 'This tournament is currently in progress. Lineups are locked.'
                : tournamentStatus === 'concluded'
                ? 'This tournament has concluded. Lineups are locked.'
                : 'This tournament is complete. Lineups are locked.'}
            </span>
          </div>
        )}

        {selectedTournament && (
          <div className="tournament-info-bar">
            <div className="info-bar-name">{selectedTournament.name}</div>
            <div className="info-bar-details">
              {selectedTournament.course} {'\u2022'} {selectedTournament.location} {'\u2022'} Par {selectedTournament.par} {'\u2022'} {selectedTournament.yardage.toLocaleString()} yards
            </div>
            {tournamentStatus === 'in-progress' && (
              <div className="in-progress-badge">{'\ud83d\udfe2'} Tournament In Progress</div>
            )}
          </div>
        )}

        <div className="lineup-container">
          <div className="lineup-section">
            <div className="section-header">
              <h2 className="section-title">Active Lineup (Select 1-4)</h2>
              {!isLocked && (
                <>
                  {!editMode ? (
                    <button onClick={() => setEditMode(true)} className="edit-btn"><Edit2 size={16} /> Edit</button>
                  ) : (
                    <div className="edit-actions">
                      <button onClick={saveLineup} className="save-btn" disabled={selectedLineup.length === 0}>
                        <Check size={16} /> Save ({selectedLineup.length}/4)
                      </button>
                      <button onClick={() => {
                        setEditMode(false);
                        setSelectedLineup(selectedParticipant.weeklyLineups?.[selectedTournamentId] || []);
                      }} className="cancel-btn"><X size={16} /> Cancel</button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="lineup-slots-grid">
              {[0, 1, 2, 3].map(index => {
                const golferId = selectedLineup[index];
                const golfer = golferId ? golfers.find(g => g.id === golferId) : null;
                return (
                  <div key={index} className={`lineup-slot ${golfer ? 'filled' : 'empty'}`}>
                    {golfer ? (
                      <>
                        <div className="slot-number">#{index + 1}</div>
                        <div className="slot-golfer">
                          <div className="slot-rank">OWGR #{getOwgr(golfer)}</div>
                          {getFedex(golfer) && <div className="slot-fedex">FedEx #{getFedex(golfer)}</div>}
                          <div className="slot-name">{golfer.name}</div>
                        </div>
                      </>
                    ) : (
                      <div className="slot-empty-text">{isLocked ? 'No golfer' : 'Empty Slot'}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="roster-section">
            <h2 className="section-title">Your Roster</h2>
            <div className="roster-grid-2col">
              {participantGolfers.map(golfer => {
                const isInLineup = selectedLineup.includes(golfer.id);
                const playerStats = sgStats[normalizeName(golfer.name)] || sgStats[golfer.name] || {};
                const courseHistory = getGolferCourseHistory(golfer.name, selectedTournament?.tournamentId);

                return (
                  <div key={golfer.id}
                    className={`roster-card ${isInLineup ? 'in-lineup' : ''} ${editMode && !isLocked ? 'editable' : ''}`}
                    onClick={() => editMode && !isLocked && toggleGolferInLineup(golfer.id)}>
                    <div className="roster-card-header">
                      <div className="roster-card-name">{golfer.name}</div>
                      <div className="roster-card-rankings">
                        <span className="roster-card-rank">#{getOwgr(golfer)} OWGR</span>
                        {getFedex(golfer) && <span className="roster-card-fedex">#{getFedex(golfer)} FedEx</span>}
                      </div>
                      {isInLineup && <div className="in-lineup-badge">In Lineup</div>}
                    </div>
                    <div className="roster-card-stats-expanded">
                      <div className="stat-item"><span className="stat-label">SG Total</span><span className="stat-value">{playerStats.sg_total ? playerStats.sg_total.toFixed(2) : '-'}{playerStats.sg_total_rank && <span className="stat-rank"> ({playerStats.sg_total_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">SG OTT</span><span className="stat-value">{playerStats.sg_ott ? playerStats.sg_ott.toFixed(2) : '-'}{playerStats.sg_ott_rank && <span className="stat-rank"> ({playerStats.sg_ott_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">SG APP</span><span className="stat-value">{playerStats.sg_app ? playerStats.sg_app.toFixed(2) : '-'}{playerStats.sg_app_rank && <span className="stat-rank"> ({playerStats.sg_app_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">SG ARG</span><span className="stat-value">{playerStats.sg_arg ? playerStats.sg_arg.toFixed(2) : '-'}{playerStats.sg_arg_rank && <span className="stat-rank"> ({playerStats.sg_arg_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">SG Putt</span><span className="stat-value">{playerStats.sg_putt ? playerStats.sg_putt.toFixed(2) : '-'}{playerStats.sg_putt_rank && <span className="stat-rank"> ({playerStats.sg_putt_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">Drive Dist</span><span className="stat-value">{playerStats.driving_dist ? playerStats.driving_dist.toFixed(1) : '-'}{playerStats.driving_dist_rank && <span className="stat-rank"> ({playerStats.driving_dist_rank})</span>}</span></div>
                      <div className="stat-item"><span className="stat-label">Drive Acc</span><span className="stat-value">{playerStats.driving_acc ? `${(playerStats.driving_acc * 100).toFixed(1)}%` : '-'}{playerStats.driving_acc_rank && <span className="stat-rank"> ({playerStats.driving_acc_rank})</span>}</span></div>
                    </div>
                    {courseHistory && (
                      <div className="course-history-section">
                        <div className="course-history-header">
                          <span className="course-name">{'\ud83d\udccd'} {courseHistory.courseName}</span>
                          <span className="course-history-label">Recent Finishes</span>
                        </div>
                        <div className="course-history-results">
                          {courseHistory.history.slice(0, 3).map((result, idx) => (
                            <div key={idx} className="history-result-simple">
                              <span className="history-year">{result.year}</span>
                              <span className="history-finish-display">{result.finish}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="admin-results-panel">
            <h2 className="section-title">Admin: Enter Tournament Results</h2>
            <div className="admin-tournament-select">
              {TOURNAMENTS.filter(t => {
                const s = getTournamentStatus(t);
                return s === 'completed' || s === 'concluded';
              }).map(t => (
                <button key={t.id} className="admin-tournament-btn" onClick={() => {
                  const results = {};
                  PARTICIPANT_NAMES.forEach(name => {
                    const val = prompt(`Enter earnings for ${name} at ${t.name}:`);
                    if (val !== null && val !== '') {
                      results[name] = { earnings: parseFloat(val) || 0, majorEarnings: 0, lineup: [] };
                    }
                  });
                  if (Object.keys(results).length > 0) {
                    setTournamentHistory(prev => ({ ...prev, [t.id]: { ...prev[t.id], ...results } }));
                  }
                }}>
                  Enter Results: {t.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="login-gate">
        <div className="login-card">
          <Trophy size={48} color="#d4af37" />
          <h1 className="login-title">The Swimming Pool</h1>
          <p className="login-subtitle">Fantasy Golf League</p>
          <form onSubmit={handleLogin}>
            <div className="login-input-wrapper">
              <Lock size={18} />
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                placeholder="Enter password"
                className="login-input"
                autoFocus
              />
            </div>
            {passwordError && <p className="login-error">Stack's a Bitch</p>}
            <button type="submit" className="login-button">Enter</button>
          </form>
        </div>
        <style>{`
          .login-gate { min-height: 100vh; background: linear-gradient(135deg, #0a1e14 0%, #1a3a2a 50%, #0a1e14 100%); display: flex; align-items: center; justify-content: center; font-family: 'Inter', -apple-system, sans-serif; }
          .login-card { background: rgba(255,255,255,0.05); border: 2px solid rgba(212,175,55,0.3); border-radius: 16px; padding: 3rem; text-align: center; width: 360px; backdrop-filter: blur(10px); }
          .login-title { color: #d4af37; font-size: 1.8rem; margin: 1rem 0 0.25rem; }
          .login-subtitle { color: rgba(255,255,255,0.5); font-size: 0.95rem; margin-bottom: 2rem; }
          .login-input-wrapper { display: flex; align-items: center; gap: 0.75rem; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 1rem; color: rgba(255,255,255,0.5); }
          .login-input { background: none; border: none; outline: none; color: #fff; font-size: 1rem; width: 100%; }
          .login-input::placeholder { color: rgba(255,255,255,0.3); }
          .login-error { color: #ff6b6b; font-size: 0.85rem; margin: -0.5rem 0 0.75rem; }
          .login-button { width: 100%; padding: 0.75rem; background: linear-gradient(135deg, #d4af37, #b8962e); color: #0a1e14; font-weight: 700; font-size: 1rem; border: none; border-radius: 8px; cursor: pointer; transition: opacity 0.2s; }
          .login-button:hover { opacity: 0.9; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="fantasy-golf-app">
      <nav className="navbar">
        <div className="nav-brand">
          <Trophy size={28} />
          <span className="brand-text">The Swimming Pool</span>
        </div>
        <div className="nav-links">
          <button onClick={() => setCurrentPage('home')}
            className={`nav-link ${currentPage === 'home' ? 'active' : ''}`}>Home</button>
          <button onClick={() => setCurrentPage('lineup')}
            className={`nav-link ${currentPage === 'lineup' ? 'active' : ''}`}>Lineup</button>
          <button onClick={() => setCurrentPage('draft')}
            className={`nav-link ${currentPage === 'draft' ? 'active' : ''}`}>Draft Board</button>
        </div>
        {isAdmin && (
          <div className="admin-controls">
            <span className="admin-badge"><Shield size={14} /> ADMIN</span>
            <button className="admin-exit-btn" onClick={() => {
              sessionStorage.removeItem('sp_admin');
              setIsAdmin(false);
            }}>Exit Admin</button>
          </div>
        )}
      </nav>

      <main className="main-content">
        {currentPage === 'home' && <HomePage />}
        {currentPage === 'draft' && <DraftPage />}
        {currentPage === 'lineup' && <LineupPage />}
      </main>

      {showRecap && <WeeklyRecap tournamentId={showRecap} tournamentHistory={tournamentHistory} onClose={() => setShowRecap(null)} />}

      <style jsx>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        :root {
          --gold: #d4af37;
          --gold-light: #f4d03f;
          --bg-dark: #0a1e14;
          --bg-card: rgba(15, 56, 35, 0.35);
          --bg-glass: rgba(10, 30, 20, 0.6);
          --glass-border: rgba(255, 255, 255, 0.08);
          --text-primary: #ffffff;
          --text-secondary: #c0c0c0;
          --text-muted: rgba(255, 255, 255, 0.5);
          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --shadow-card: 0 4px 24px rgba(0, 0, 0, 0.3);
          --shadow-hover: 0 8px 32px rgba(212, 175, 55, 0.15);
          --transition-fast: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          --transition-medium: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .fantasy-golf-app { min-height: 100vh; background: linear-gradient(135deg, #0a4d2e 0%, #1a5f3f 50%, #0f3823 100%); font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; color: var(--text-primary); }
        .navbar { background: rgba(10, 30, 20, 0.95); backdrop-filter: blur(16px); padding: 1rem 2rem; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid var(--gold); position: sticky; top: 0; z-index: 100; box-shadow: 0 4px 30px rgba(0, 0, 0, 0.4); }
        .nav-brand { display: flex; align-items: center; gap: 0.75rem; color: var(--gold); }
        .brand-text { font-size: 1.4rem; font-weight: 800; letter-spacing: 0.5px; }
        .nav-links { display: flex; gap: 0.5rem; }
        .nav-link { background: none; border: none; color: #e8e8e8; font-size: 0.9rem; font-family: 'Inter', sans-serif; padding: 0.6rem 1.25rem; cursor: pointer; border-radius: var(--radius-sm); transition: var(--transition-fast); font-weight: 500; }
        .nav-link:hover { background: rgba(212, 175, 55, 0.1); color: var(--gold); }
        .nav-link.active { background: linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 100%); color: #0a3020; font-weight: 700; box-shadow: 0 0 20px rgba(212, 175, 55, 0.3); }
        .admin-controls { display: flex; align-items: center; gap: 0.75rem; }
        .admin-badge { display: flex; align-items: center; gap: 0.4rem; background: #e74c3c; color: white; padding: 0.35rem 0.75rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.5px; }
        .admin-exit-btn { background: rgba(231, 76, 60, 0.15); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.4); padding: 0.35rem 0.75rem; border-radius: var(--radius-sm); font-size: 0.75rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: var(--transition-fast); }
        .admin-exit-btn:hover { background: #e74c3c; color: white; }
        .main-content { padding: 1.5rem 2rem; max-width: 1400px; margin: 0 auto; }
        .splash-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(5, 15, 10, 0.95); display: flex; align-items: flex-start; justify-content: center; padding-top: 2rem; z-index: 500; animation: splashFade 8s ease-in forwards; cursor: pointer; }
        .splash-content { position: relative; max-width: 750px; width: 92%; text-align: center; animation: splashZoom 8s ease-in forwards; }
        .splash-image { width: 100%; border-radius: 16px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(212, 175, 55, 0.2); }
        .splash-skip-hint { margin-top: 1.25rem; color: rgba(255, 255, 255, 0.4); font-size: 0.8rem; font-family: 'Inter', sans-serif; letter-spacing: 0.5px; }
        @keyframes splashFade { 0%, 80% { opacity: 1; } 100% { opacity: 0; pointer-events: none; } }
        @keyframes splashZoom { 0% { transform: scale(0.9); opacity: 0; } 5% { transform: scale(1); opacity: 1; } 80% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.02); opacity: 0; } }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; margin-bottom: 2.5rem; }
        .stat-card { background: var(--bg-card); backdrop-filter: blur(12px); padding: 1.75rem; border-radius: var(--radius-lg); border: 1px solid var(--glass-border); transition: var(--transition-medium); box-shadow: var(--shadow-card); animation: fadeInUp 0.5s ease both; }
        .stat-card:hover { transform: translateY(-4px); border-color: rgba(212, 175, 55, 0.3); box-shadow: var(--shadow-hover); }
        .leader-card { background: linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, var(--bg-card) 100%); border-color: rgba(212, 175, 55, 0.25); }
        .stat-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem; }
        .stat-icon { color: var(--gold); }
        .stat-header h3 { font-size: 0.65rem; color: var(--text-muted); font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; }
        .leader-info { text-align: center; }
        .leader-name { font-size: 1.6rem; font-weight: 800; color: var(--gold); margin-bottom: 0.4rem; }
        .leader-winnings { font-size: 2.2rem; color: var(--text-primary); font-weight: 800; }
        .tournament-date { font-size: 0.9rem; color: var(--text-secondary); }
        .section-title { font-size: 1.5rem; font-weight: 800; color: var(--gold); margin-bottom: 1.5rem; padding-bottom: 0.75rem; border-bottom: 2px solid rgba(212, 175, 55, 0.2); }
        .leaderboard-section { margin-bottom: 3rem; }
        .leaderboard { background: var(--bg-glass); backdrop-filter: blur(12px); border-radius: var(--radius-lg); overflow: hidden; border: 1px solid var(--glass-border); box-shadow: var(--shadow-card); }
        .leaderboard-header { display: grid; grid-template-columns: 60px 1fr 160px; padding: 0.75rem 1rem; background: rgba(212, 175, 55, 0.08); font-weight: 700; color: var(--gold); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1.5px; }
        .leaderboard-row { display: grid; grid-template-columns: 60px 1fr 160px; padding: 0.75rem 1rem; border-top: 1px solid rgba(255, 255, 255, 0.04); transition: var(--transition-fast); }
        .leaderboard-row:hover { background: rgba(212, 175, 55, 0.04); }
        .leaderboard-row.leader { background: linear-gradient(90deg, rgba(212, 175, 55, 0.12) 0%, transparent 100%); border-left: 3px solid var(--gold); }
        .leaderboard-row.expanded { background: rgba(212, 175, 55, 0.1); }
        .rank-col { display: flex; align-items: center; font-weight: 700; color: var(--gold); font-size: 0.95rem; }
        .name-col { display: flex; align-items: center; gap: 0.75rem; font-size: 1rem; font-weight: 600; }
        .winnings-col { font-family: 'Inter', sans-serif; font-size: 1rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem; }
        .leaderboard-movement { font-size: 0.7rem; font-weight: 700; min-width: 24px; text-align: center; }
        .leaderboard-movement.up { color: #2ecc71; }
        .leaderboard-movement.down { color: #e74c3c; }
        .leaderboard-movement.flat { color: var(--text-muted); font-size: 0.65rem; }
        .avatar-circle { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.75rem; color: white; flex-shrink: 0; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); }
        .avatar-has-photo { cursor: pointer; }
        .avatar-hover-preview { display: none; position: fixed; z-index: 300; background: rgba(10, 30, 20, 0.95); border: 2px solid rgba(212, 175, 55, 0.4); border-radius: var(--radius-lg); padding: 6px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6); pointer-events: none; animation: scaleIn 0.15s ease; }
        .avatar-hover-preview img { width: 160px; height: auto; max-height: 240px; object-fit: contain; border-radius: var(--radius-md); display: block; }
        .avatar-hover-name { text-align: center; padding: 0.4rem 0 0.15rem; font-size: 0.8rem; font-weight: 700; color: var(--gold); }
        .home-layout { display: grid; grid-template-columns: 1fr 300px 320px; gap: 1.5rem; }
        .home-left { min-width: 0; }
        .home-right { min-width: 0; }
        .home-center { display: flex; flex-direction: column; gap: 0.75rem; min-width: 0; }
        .tournament-schedule { margin-bottom: 3rem; }
        .tournament-list { display: flex; flex-direction: column; gap: 0.75rem; }
        .tournament-card { background: var(--bg-card); backdrop-filter: blur(12px); padding: 1rem; border-radius: var(--radius-md); border: 1px solid var(--glass-border); position: relative; transition: var(--transition-medium); box-shadow: var(--shadow-card); }
        .tournament-card:hover { transform: translateY(-3px); border-color: rgba(212, 175, 55, 0.25); box-shadow: var(--shadow-hover); }
        .tournament-card.major { background: linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, var(--bg-card) 100%); border-color: rgba(212, 175, 55, 0.2); }
        .tournament-card.completed { opacity: 0.65; }
        .tournament-type-badge { position: absolute; top: 0.75rem; right: 0.75rem; padding: 0.2rem 0.6rem; background: var(--gold); color: #0a3020; border-radius: 20px; font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        .tournament-name { font-size: 1rem; font-weight: 700; margin-bottom: 0.35rem; color: var(--text-primary); }
        .tournament-course { font-size: 0.8rem; color: var(--gold); margin-bottom: 0.2rem; font-weight: 600; }
        .tournament-location { font-size: 0.75rem; color: var(--text-muted); margin-bottom: 0.35rem; }
        .tournament-specs { display: flex; align-items: center; gap: 0.5rem; font-size: 0.75rem; color: rgba(255, 255, 255, 0.6); margin-bottom: 0.5rem; font-family: 'Inter', sans-serif; font-weight: 500; }
        .spec-separator { color: rgba(212, 175, 55, 0.4); }
        .status-pill { display: inline-flex; align-items: center; gap: 0.4rem; margin-top: 0.75rem; padding: 0.35rem 0.85rem; border-radius: 20px; font-weight: 600; font-size: 0.75rem; }
        .status-pill.live { background: rgba(46, 204, 113, 0.15); color: #2ecc71; border: 1px solid rgba(46, 204, 113, 0.3); }
        .status-pill.completed { background: rgba(255, 255, 255, 0.06); color: var(--text-muted); border: 1px solid rgba(255, 255, 255, 0.1); }
        .pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #2ecc71; animation: pulseDot 1.5s ease-in-out infinite; }
        @keyframes pulseDot { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.4); } 50% { opacity: 0.6; box-shadow: 0 0 0 6px rgba(46, 204, 113, 0); } }
        .recap-btn { display: block; margin-top: 0.5rem; padding: 0.4rem 0.85rem; background: rgba(212, 175, 55, 0.1); color: var(--gold); border: 1px solid rgba(212, 175, 55, 0.3); border-radius: 20px; font-size: 0.75rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: var(--transition-fast); }
        .recap-btn:hover { background: var(--gold); color: #0a3020; }
        .completed-badge { margin-top: 1rem; padding: 0.5rem; background: rgba(46, 204, 113, 0.2); color: #2ecc71; border-radius: 6px; font-weight: 600; font-size: 0.85rem; text-align: center; }
        .in-progress-badge { margin-top: 0.75rem; padding: 0.4rem 0.85rem; background: rgba(46, 204, 113, 0.15); color: #2ecc71; border-radius: 20px; font-weight: 600; font-size: 0.8rem; text-align: center; display: inline-flex; align-items: center; gap: 0.4rem; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .draft-page, .lineup-page { animation: fadeInUp 0.4s ease both; }
        .home-page { animation: fadeInUp 0.4s ease both; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .page-title { font-size: 2rem; color: var(--gold); margin-bottom: 0.5rem; font-weight: 800; }
        .draft-header { margin-bottom: 2rem; }
        .draft-info { color: var(--text-secondary); font-size: 1rem; margin: 0; }
        .section-title-large { font-size: 1.5rem; color: var(--gold); margin-bottom: 1.5rem; font-weight: 700; }
        .teams-grid-wide { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.75rem; margin-bottom: 2rem; }
        .team-card-compact { background: var(--bg-card); backdrop-filter: blur(12px); border-radius: var(--radius-md); border: 1px solid var(--glass-border); overflow: hidden; min-height: 240px; box-shadow: var(--shadow-card); transition: var(--transition-medium); }
        .team-card-compact:hover { transform: translateY(-4px); box-shadow: var(--shadow-hover); border-color: rgba(212, 175, 55, 0.2); }
        .team-header-compact { background: rgba(212, 175, 55, 0.08); padding: 0.65rem 0.75rem; display: flex; justify-content: space-between; align-items: center; }
        .team-name-compact { color: var(--gold); font-weight: 700; font-size: 0.85rem; }
        .team-count-badge { background: var(--gold); color: #0a3020; padding: 0.15rem 0.5rem; border-radius: 12px; font-size: 0.65rem; font-weight: 700; }
        .team-roster-compact { padding: 0.6rem; }
        .roster-golfer-compact { display: flex; justify-content: space-between; align-items: center; padding: 0.4rem 0.5rem; margin-bottom: 0.3rem; background: rgba(255, 255, 255, 0.03); border-radius: 6px; border-left: 2px solid rgba(212, 175, 55, 0.4); }
        .golfer-info-compact { display: flex; flex-direction: column; gap: 0.1rem; flex: 1; min-width: 0; }
        .golfer-rank-small { color: var(--gold); font-weight: 700; font-size: 0.65rem; }
        .golfer-name-small { color: var(--text-primary); font-size: 0.75rem; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .roster-empty-compact { color: #808080; text-align: center; padding: 1.5rem 0.5rem; font-style: italic; font-size: 0.8rem; }
        .available-golfers-section { margin-top: 3rem; }
        .search-box { display: flex; gap: 1rem; align-items: center; margin-bottom: 1.5rem; }
        .search-input { flex: 1; padding: 0.85rem 1rem; background: var(--bg-glass); border: 1px solid var(--glass-border); border-radius: var(--radius-md); color: var(--text-primary); font-size: 0.95rem; font-family: 'Inter', sans-serif; transition: var(--transition-fast); }
        .search-input:focus { outline: none; border-color: rgba(212, 175, 55, 0.4); box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1); }
        .search-input::placeholder { color: #808080; }
        .golfer-count { color: var(--gold); font-weight: 600; white-space: nowrap; font-size: 0.9rem; }
        .golfers-grid-wide { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.75rem; max-height: 600px; overflow-y: auto; padding-right: 0.5rem; }
        .golfer-card-wide { background: var(--bg-card); backdrop-filter: blur(12px); padding: 0.75rem; border-radius: var(--radius-sm); border: 1px solid var(--glass-border); transition: var(--transition-fast); display: flex; flex-direction: column; gap: 0.3rem; }
        .golfer-card-wide:hover { border-color: rgba(212, 175, 55, 0.3); transform: translateY(-2px); box-shadow: var(--shadow-hover); }
        .golfer-card-wide.top-50 { border-color: rgba(255, 215, 0, 0.25); background: rgba(255, 215, 0, 0.04); }
        .golfer-card-wide.top-70 { border-color: rgba(192, 192, 192, 0.25); background: rgba(192, 192, 192, 0.04); }
        .golfer-rank-badge-wide { color: var(--gold); font-weight: 700; font-size: 0.8rem; }
        .golfer-name-wide { color: var(--text-primary); font-size: 0.9rem; font-weight: 500; }
        .golfer-status-wide { font-size: 0.75rem; }
        .available-label { color: #2ecc71; font-weight: 600; }
        .lineup-header { margin-bottom: 1.5rem; }
        .participant-selector { margin-bottom: 1.5rem; display: flex; align-items: center; gap: 1rem; }
        .participant-selector label { font-size: 0.95rem; font-weight: 600; color: var(--gold); }
        .participant-select { padding: 0.65rem 1rem; background: var(--bg-glass); border: 1px solid var(--glass-border); border-radius: var(--radius-sm); color: var(--text-primary); font-size: 0.95rem; font-family: 'Inter', sans-serif; cursor: pointer; min-width: 200px; transition: var(--transition-fast); }
        .participant-select:focus { outline: none; border-color: rgba(212, 175, 55, 0.4); }
        .tournament-tabs { display: flex; gap: 0.4rem; overflow-x: auto; padding-bottom: 0.5rem; margin-bottom: 1.5rem; scrollbar-width: thin; scrollbar-color: var(--gold) transparent; }
        .tournament-tabs::-webkit-scrollbar { height: 4px; }
        .tournament-tabs::-webkit-scrollbar-thumb { background: rgba(212, 175, 55, 0.3); border-radius: 2px; }
        .tournament-tab { display: flex; flex-direction: column; align-items: center; padding: 0.6rem 0.85rem; background: var(--bg-card); backdrop-filter: blur(12px); border: 1px solid var(--glass-border); border-radius: var(--radius-sm); color: var(--text-secondary); cursor: pointer; min-width: 110px; position: relative; transition: var(--transition-fast); font-family: 'Inter', sans-serif; }
        .tournament-tab:hover { border-color: rgba(212, 175, 55, 0.3); background: rgba(15, 56, 35, 0.5); }
        .tournament-tab.active { border-color: var(--gold); color: var(--text-primary); background: linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, var(--bg-card) 100%); box-shadow: 0 0 16px rgba(212, 175, 55, 0.1); }
        .tournament-tab.in-progress { border-color: rgba(46, 204, 113, 0.4); }
        .tournament-tab.completed, .tournament-tab.concluded { opacity: 0.55; }
        .tab-name { font-weight: 600; font-size: 0.7rem; text-align: center; }
        .tab-date { font-size: 0.6rem; color: var(--text-muted); margin-top: 0.2rem; }
        .tab-lock-icon { position: absolute; top: 4px; right: 4px; color: #e74c3c; }
        .tab-status-dot { width: 6px; height: 6px; border-radius: 50%; margin-top: 0.3rem; }
        .tab-status-dot.live { background: #2ecc71; animation: pulseDot 1.5s ease-in-out infinite; }
        .tab-status-dot.done { background: rgba(255, 255, 255, 0.3); }
        .lock-banner { display: flex; align-items: center; gap: 0.75rem; padding: 0.85rem 1rem; background: rgba(231, 76, 60, 0.08); border: 1px solid rgba(231, 76, 60, 0.2); border-radius: var(--radius-md); color: #e74c3c; font-weight: 600; margin-bottom: 1.5rem; font-size: 0.9rem; }
        .tournament-info-bar { background: rgba(212, 175, 55, 0.06); border: 1px solid rgba(212, 175, 55, 0.15); border-radius: var(--radius-md); padding: 1rem 1.25rem; margin-bottom: 1.5rem; }
        .info-bar-name { font-size: 1.15rem; font-weight: 700; color: var(--gold); margin-bottom: 0.25rem; }
        .info-bar-details { font-size: 0.8rem; color: var(--text-muted); }
        .lineup-container { display: grid; grid-template-columns: 2fr 3fr; gap: 1.5rem; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
        .edit-btn, .save-btn, .cancel-btn { display: flex; align-items: center; gap: 0.4rem; padding: 0.6rem 1.25rem; border: none; border-radius: var(--radius-sm); font-family: 'Inter', sans-serif; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: var(--transition-fast); }
        .edit-btn { background: rgba(212, 175, 55, 0.1); color: var(--gold); border: 1px solid rgba(212, 175, 55, 0.3); }
        .edit-btn:hover { background: var(--gold); color: #0a3020; }
        .save-btn { background: #2ecc71; color: white; }
        .save-btn:hover:not(:disabled) { background: #27ae60; }
        .save-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cancel-btn { background: rgba(231, 76, 60, 0.15); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.3); }
        .cancel-btn:hover { background: #e74c3c; color: white; }
        .edit-actions { display: flex; gap: 0.5rem; }
        .lineup-slots-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .lineup-slots { display: grid; gap: 1rem; }
        .lineup-slot { background: var(--bg-card); backdrop-filter: blur(12px); padding: 1.25rem; border-radius: var(--radius-md); border: 1px solid var(--glass-border); min-height: 100px; display: flex; align-items: center; gap: 1rem; transition: var(--transition-medium); box-shadow: var(--shadow-card); }
        .lineup-slot:hover { transform: translateY(-2px); }
        .lineup-slot.filled { background: linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, var(--bg-card) 100%); border-color: rgba(212, 175, 55, 0.2); }
        .slot-number { font-size: 1.8rem; font-weight: 800; color: var(--gold); }
        .slot-golfer { flex: 1; }
        .slot-rank { color: var(--text-muted); font-size: 0.75rem; margin-bottom: 0.15rem; font-weight: 500; }
        .slot-name { font-size: 1.05rem; font-weight: 700; margin-bottom: 0.15rem; }
        .slot-empty-text { color: var(--text-muted); font-style: italic; text-align: center; width: 100%; font-size: 0.9rem; }
        .roster-grid-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .roster-grid { display: grid; gap: 1rem; }
        .roster-card { background: var(--bg-card); backdrop-filter: blur(12px); padding: 1.1rem; border-radius: var(--radius-md); border: 1px solid var(--glass-border); transition: var(--transition-medium); position: relative; box-shadow: var(--shadow-card); }
        .roster-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-hover); }
        .roster-card-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 0.85rem; }
        .roster-card.editable { cursor: pointer; }
        .roster-card.editable:hover { border-color: rgba(212, 175, 55, 0.3); }
        .roster-card.in-lineup { background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, var(--bg-card) 100%); border-color: rgba(212, 175, 55, 0.2); }
        .roster-card-rank { color: var(--gold); font-weight: 700; font-size: 1.1rem; min-width: 50px; }
        .roster-card-name { font-size: 1rem; font-weight: 700; flex: 1; }
        .roster-card-stats-expanded { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.6rem; padding-top: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.06); }
        .stat-item { display: flex; flex-direction: column; gap: 0.2rem; }
        .stat-label { font-size: 0.6rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; }
        .stat-value { font-size: 0.9rem; font-weight: 800; color: var(--gold); font-family: 'Inter', sans-serif; display: flex; align-items: baseline; gap: 0.2rem; }
        .stat-rank { font-size: 0.7rem; color: rgba(212, 175, 55, 0.5); font-weight: 500; }
        .in-lineup-badge { position: absolute; top: 0.6rem; right: 0.6rem; background: var(--gold); color: #0a3020; padding: 0.2rem 0.6rem; border-radius: 20px; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.3px; }
        .course-history-section { margin-top: 0.85rem; padding-top: 0.85rem; border-top: 1px solid rgba(255, 255, 255, 0.06); }
        .course-history-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.6rem; }
        .course-name { font-size: 0.8rem; color: rgba(255, 255, 255, 0.6); font-weight: 500; }
        .course-history-label { font-size: 0.65rem; color: var(--text-muted); }
        .course-history-results { display: flex; flex-direction: column; gap: 0.35rem; }
        .history-result-simple { display: grid; grid-template-columns: 50px 1fr; gap: 0.75rem; align-items: center; padding: 0.35rem 0.6rem; background: rgba(255, 255, 255, 0.02); border-radius: 4px; font-size: 0.8rem; }
        .history-year { color: var(--text-muted); font-weight: 600; font-size: 0.75rem; }
        .history-finish-display { color: var(--gold); font-weight: 700; font-size: 0.9rem; }
        .participant-details { background: rgba(10, 30, 20, 0.9); backdrop-filter: blur(12px); padding: 1.5rem; margin-top: -1px; border: 1px solid var(--glass-border); border-top: none; border-radius: 0 0 var(--radius-md) var(--radius-md); animation: slideDown 0.3s ease-out; }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .details-section { margin-bottom: 1.5rem; }
        .details-section:last-child { margin-bottom: 0; }
        .details-section h3 { color: var(--gold); font-size: 1rem; font-weight: 700; margin-bottom: 0.75rem; padding-bottom: 0.4rem; border-bottom: 1px solid rgba(212, 175, 55, 0.2); }
        .golfer-list { display: grid; gap: 0.5rem; }
        .golfer-item { background: rgba(255, 255, 255, 0.03); padding: 0.75rem 1rem; border-radius: var(--radius-sm); display: grid; grid-template-columns: 60px 1fr auto; align-items: center; gap: 0.75rem; border: 1px solid rgba(255, 255, 255, 0.06); transition: var(--transition-fast); }
        .golfer-item:hover { background: rgba(255, 255, 255, 0.05); border-color: rgba(212, 175, 55, 0.2); }
        .golfer-item.lineup-item { border-left: 3px solid var(--gold); }
        .golfer-item.bench-item { border-left: 3px solid rgba(255, 255, 255, 0.2); opacity: 0.8; }
        .golfer-item.bench-item:hover { opacity: 1; }
        .golfer-rank { color: var(--gold); font-weight: 600; font-size: 0.95rem; }
        .golfer-name { color: var(--text-primary); font-weight: 500; font-size: 0.9rem; }
        .golfer-list-header { display: grid; grid-template-columns: 1fr 70px 70px 70px; align-items: center; gap: 0.75rem; padding: 0.4rem 1rem; border-bottom: 1px solid rgba(212, 175, 55, 0.2); margin-bottom: 0.25rem; }
        .golfer-header-name, .golfer-header-pos, .golfer-header-score, .golfer-header-thru { color: rgba(212, 175, 55, 0.7); font-weight: 700; font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.8px; }
        .golfer-header-pos, .golfer-header-score, .golfer-header-thru { text-align: center; }
        .golfer-item.has-live-data { grid-template-columns: 1fr 70px 70px 70px; padding: 0.65rem 1rem; }
        .golfer-pos-val { color: var(--text-primary); font-weight: 600; font-size: 0.85rem; text-align: center; }
        .golfer-score-val { color: #4ecdc4; font-weight: 700; font-size: 0.85rem; text-align: center; }
        .golfer-thru-val { color: var(--text-muted); font-weight: 500; font-size: 0.8rem; text-align: center; }
        .slot-fedex { color: var(--text-muted); font-size: 0.7rem; font-weight: 500; }
        .roster-card-rankings { display: flex; gap: 0.75rem; margin-top: 0.2rem; }
        .roster-card-rank { color: var(--gold); font-size: 0.75rem; font-weight: 600; }
        .roster-card-fedex { color: var(--text-muted); font-size: 0.75rem; font-weight: 500; }
        .no-data { color: var(--text-muted); font-style: italic; padding: 0.85rem; text-align: center; background: rgba(255, 255, 255, 0.02); border-radius: var(--radius-sm); font-size: 0.85rem; }
        .admin-results-panel { margin-top: 2rem; padding: 1.5rem; background: rgba(231, 76, 60, 0.05); border: 1px solid rgba(231, 76, 60, 0.2); border-radius: var(--radius-md); }
        .admin-tournament-select { display: flex; flex-wrap: wrap; gap: 0.5rem; }
        .admin-tournament-btn { padding: 0.6rem 1rem; background: rgba(231, 76, 60, 0.1); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.3); border-radius: var(--radius-sm); font-family: 'Inter', sans-serif; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: var(--transition-fast); }
        .admin-tournament-btn:hover { background: #e74c3c; color: white; }
        .recap-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; animation: fadeInUp 0.3s ease; }
        .recap-modal { background: linear-gradient(135deg, #0f3823 0%, #0a1e14 100%); border: 1px solid rgba(212, 175, 55, 0.25); border-radius: var(--radius-xl); padding: 2rem; max-width: 520px; width: 90%; max-height: 80vh; overflow-y: auto; position: relative; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5); }
        .recap-close { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: var(--text-muted); font-size: 1.5rem; cursor: pointer; padding: 0.25rem 0.5rem; transition: var(--transition-fast); }
        .recap-close:hover { color: var(--text-primary); }
        .recap-title { color: var(--gold); font-size: 1.3rem; font-weight: 800; margin-bottom: 1.5rem; text-align: center; }
        .recap-biggest { text-align: center; padding: 1.5rem; margin-bottom: 1.5rem; background: rgba(212, 175, 55, 0.08); border-radius: var(--radius-md); border: 1px solid rgba(212, 175, 55, 0.15); }
        .recap-biggest-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: var(--text-muted); margin-bottom: 0.75rem; }
        .recap-biggest-avatar { width: 56px; height: 56px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1.3rem; color: white; margin-bottom: 0.5rem; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3); }
        .recap-biggest-name { font-size: 1.3rem; font-weight: 800; color: var(--gold); margin-bottom: 0.25rem; }
        .recap-biggest-amount { font-size: 1.8rem; font-weight: 800; color: var(--text-primary); }
        .recap-earnings-list { display: flex; flex-direction: column; gap: 0.4rem; }
        .recap-earnings-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.65rem 0.85rem; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-sm); border: 1px solid rgba(255, 255, 255, 0.04); transition: var(--transition-fast); }
        .recap-earnings-row:hover { background: rgba(255, 255, 255, 0.06); }
        .recap-rank { color: var(--text-muted); font-weight: 700; font-size: 0.8rem; min-width: 28px; }
        .recap-avatar-small { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.7rem; color: white; flex-shrink: 0; }
        .recap-name { flex: 1; font-weight: 600; font-size: 0.9rem; }
        .recap-amount { font-weight: 700; color: var(--gold); font-size: 0.9rem; }
        .recap-movement { font-weight: 700; font-size: 0.8rem; min-width: 32px; text-align: center; }
        .recap-movement.up { color: #2ecc71; }
        .recap-movement.down { color: #e74c3c; }
        .recap-earnings-row.expandable { cursor: pointer; }
        .recap-earnings-row.expanded { background: rgba(255, 255, 255, 0.08); border-color: rgba(255, 255, 255, 0.1); }
        .recap-expand-icon { font-size: 0.7rem; color: var(--text-muted); transition: transform 0.2s ease; min-width: 16px; text-align: center; }
        .recap-earnings-row.expanded .recap-expand-icon { transform: rotate(90deg); }
        .recap-lineup-detail { padding: 0.5rem 0.85rem 0.65rem 2.8rem; display: flex; flex-direction: column; gap: 0.3rem; margin-top: -0.3rem; margin-bottom: 0.1rem; background: rgba(255, 255, 255, 0.02); border-radius: 0 0 var(--radius-sm) var(--radius-sm); border: 1px solid rgba(255, 255, 255, 0.04); border-top: none; }
        .recap-golfer-row { display: flex; align-items: center; justify-content: space-between; padding: 0.3rem 0; border-bottom: 1px solid rgba(255, 255, 255, 0.03); }
        .recap-golfer-row:last-child { border-bottom: none; }
        .recap-golfer-name { font-size: 0.8rem; font-weight: 500; color: var(--text-secondary); }
        .recap-golfer-earnings { font-size: 0.8rem; font-weight: 700; }
        .recap-golfer-earnings.earned { color: var(--gold); }
        .recap-golfer-earnings.zero { color: var(--text-muted); font-style: italic; }
        .insight-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .insight-item { text-align: center; padding: 0.5rem; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-sm); }
        .insight-value { font-size: 1.1rem; font-weight: 800; color: var(--gold); margin-bottom: 0.2rem; }
        .insight-label { font-size: 0.6rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; }
        .race-card { display: flex; flex-direction: column; gap: 0.5rem; }
        .race-row { display: flex; align-items: center; gap: 0.6rem; }
        .race-avatar { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.7rem; color: white; flex-shrink: 0; }
        .race-name { flex: 1; font-weight: 600; font-size: 0.85rem; }
        .race-amount { font-weight: 700; color: var(--gold); font-size: 0.85rem; }
        .race-gap { text-align: center; padding: 0.3rem; }
        .race-gap-label { font-size: 0.6rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.8px; margin-right: 0.3rem; }
        .race-gap-value { font-weight: 800; color: #2ecc71; font-size: 0.9rem; }
        .biggest-week { text-align: center; padding: 0.5rem 0; }
        .biggest-week-avatar { width: 40px; height: 40px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; color: white; margin-bottom: 0.4rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); }
        .biggest-week-name { font-size: 1rem; font-weight: 700; color: var(--gold); margin-bottom: 0.15rem; }
        .biggest-week-amount { font-size: 1.4rem; font-weight: 800; color: var(--text-primary); margin-bottom: 0.15rem; }
        .biggest-week-tournament { font-size: 0.7rem; color: var(--text-muted); }
        .value-picks-card { max-height: 420px; overflow-y: auto; }
        .value-picks-subtitle { font-size: 0.6rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; margin-bottom: 0.75rem; }
        .value-picks-list { display: flex; flex-direction: column; gap: 0.4rem; }
        .value-pick-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.6rem; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-sm); border: 1px solid rgba(255, 255, 255, 0.04); transition: var(--transition-fast); }
        .value-pick-row:hover { background: rgba(255, 255, 255, 0.06); border-color: rgba(212, 175, 55, 0.2); }
        .value-pick-row:first-child { background: rgba(212, 175, 55, 0.08); border-color: rgba(212, 175, 55, 0.15); }
        .value-pick-rank { color: var(--gold); font-weight: 700; font-size: 0.75rem; min-width: 22px; }
        .value-pick-info { flex: 1; min-width: 0; }
        .value-pick-name { font-size: 0.8rem; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .value-pick-meta { font-size: 0.6rem; color: var(--text-muted); margin-top: 0.1rem; }
        .value-pick-earnings { font-weight: 700; color: var(--gold); font-size: 0.8rem; white-space: nowrap; }
        .coming-soon-card { opacity: 0.5; }
        .coming-soon-content { display: flex; flex-direction: column; gap: 0.4rem; }
        .coming-soon-item { padding: 0.5rem 0.75rem; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-sm); color: var(--text-muted); font-size: 0.8rem; font-weight: 500; border: 1px dashed rgba(255, 255, 255, 0.1); }
        @media (max-width: 1200px) { .lineup-container { grid-template-columns: 1fr; } .teams-grid-wide { grid-template-columns: repeat(3, 1fr); } .home-layout { grid-template-columns: 1fr; } .home-center { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); } .roster-grid-2col { grid-template-columns: 1fr; } }
        @media (max-width: 768px) { .main-content { padding: 1rem; } .stats-grid { grid-template-columns: 1fr; } .leaderboard-header, .leaderboard-row { grid-template-columns: 60px 1fr 100px; } .nav-links { flex-direction: column; gap: 0.3rem; } .teams-grid-wide { grid-template-columns: repeat(2, 1fr); } .tournament-tabs { gap: 0.3rem; } .tournament-tab { min-width: 85px; padding: 0.5rem 0.6rem; } .lineup-slots-grid { grid-template-columns: 1fr; } .recap-modal { padding: 1.25rem; } }
      `}</style>
    </div>
  );
};

export default FantasyGolfLeague;
